<template>
  <div class="overview-dashboard">
    <!-- Header with Filters -->
    <div class="dashboard-header">
      <div class="header-controls">
        <div class="brand-filter">
          <label for="brand-select">Brand:</label>
          <select id="brand-select" v-model="selectedBrand" @change="onBrandChange" class="brand-selector">
            <option value="">All Brands</option>
            <option v-for="brand in availableBrands" :key="brand.value" :value="brand.value">{{ brand.label }}</option>
          </select>
        </div>
        <div class="year-selectors-container">
          <div class="year-selector-group">
            <label for="primary-year">Primary Year:</label>
            <select id="primary-year" v-model="primaryYear" @change="onYearSelectionChange" class="year-selector">
              <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
            </select>
          </div>
          <div class="vs-separator">vs</div>
          <div class="year-selector-group">
            <label for="comparison-year">Compare with:</label>
            <select id="comparison-year" v-model="comparisonYear" @change="onYearSelectionChange" class="year-selector">
              <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Top Row: Revenue Overview + Revenue Composition -->
    <div class="revenue-overview-container">
      <div class="card revenue-overview-merged">
        <!-- Revenue Comparison Section (2/3) -->
        <div class="revenue-comparison-section">
          <div class="card-title-with-actions">
            <h3 class="card-title">Revenue</h3>
            <div class="button-group">
              <button @click="openScheduleChangesModal" class="changes-button">
                Last Changes
              </button>
              <button @click="refreshRevenueData" class="refresh-button" :disabled="revenueData.loading">
                <v-icon name="sync-alt" :class="{ 'spinning': revenueData.loading }"></v-icon>
              </button>
            </div>
          </div>

          <div class="revenue-comparison-content">
            <div class="revenue-comparison-header">
              <div class="year-label primary">{{ primaryYear }} (€)</div>
              <div class="year-label comparison">{{ comparisonYear }} (€)</div>
            </div>

            <div class="revenue-metric-group">
              <div class="metric-label">Total Revenue</div>
              <div class="metric-values">
                <div class="value-item primary-total">€{{ formatNumber(revenueData.total) }}</div>
                <div class="value-item">€{{ formatNumber(ytdComparison.previousFullYear) }}</div>
                <div class="value-item change" :class="revenueData.change >= 0 ? 'positive' : 'negative'">
                  {{ revenueData.change >= 0 ? '+' : '' }}{{ revenueData.change }}%
                </div>
              </div>
            </div>

            <div class="revenue-metric-group">
              <div class="metric-label">YTD Revenue</div>
              <div class="metric-values">
                <div class="value-item primary-total">€{{ formatNumber(revenueData.invoiced) }}</div>
                <div class="value-item">€{{ formatNumber(ytdComparison.previousSamePeriod) }}</div>
                <div class="value-item change" :class="ytdComparison.revenueChange >= 0 ? 'positive' : 'negative'">
                  {{ ytdComparison.revenueChange >= 0 ? '+' : '' }}{{ ytdComparison.revenueChange }}%
                </div>
              </div>
            </div>

            <div class="revenue-metric-group">
              <div class="metric-label">Scheduled</div>
              <div class="metric-values">
                <div class="value-item primary-total">€{{ formatNumber(revenueData.scheduled) }}</div>
                <div class="value-item">€{{ formatNumber(ytdComparison.previousRemaining) }}</div>
                <div class="value-item change" :class="ytdComparison.scheduledChange >= 0 ? 'positive' : 'negative'">
                  {{ ytdComparison.scheduledChange >= 0 ? '+' : '' }}{{ ytdComparison.scheduledChange }}%
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Revenue Composition Section (1/3) -->
        <div class="revenue-composition-section-merged">
          <div class="card-title-with-actions">
            <h3 class="card-title">Revenue Composition ({{ primaryYear }})</h3>
            <button @click="refreshRevenueCompositionData" class="refresh-button" :disabled="primaryYearRevenueData.loading">
              <v-icon name="sync-alt" :class="{ 'spinning': primaryYearRevenueData.loading }"></v-icon>
            </button>
          </div>

          <div class="revenue-composition-container-merged">
            <!-- Chart Section -->
            <div class="revenue-composition-chart">
              <ulu-doughnut-chart
                :chart-data="revenueCompositionChartData"
                :options="revenueCompositionChartOptions"
              />
            </div>

            <!-- Data Section -->
            <div class="revenue-composition-data">
              <div class="revenue-composition-item">
                <div class="revenue-composition-label">
                  <span class="revenue-composition-color" style="background-color: #FC8181;"></span>
                  <span class="revenue-composition-text"><strong>ARR start of period</strong></span>
                </div>
                <div class="revenue-composition-value">€{{ formatNumber(arrStartData.currentYear) }}</div>
                <div class="revenue-composition-percent">{{ revenueCompositionOldPercent }}%</div>
              </div>

              <div class="revenue-composition-item">
                <div class="revenue-composition-label">
                  <span class="revenue-composition-color" style="background-color: #48BB78;"></span>
                  <span class="revenue-composition-text"><strong>New Revenue</strong></span>
                </div>
                <div class="revenue-composition-value">€{{ formatNumber(revenueCompositionNewRevenue) }}</div>
                <div class="revenue-composition-percent">{{ revenueCompositionNewPercent }}%</div>
              </div>

              <div class="revenue-composition-item sub-item">
                <div class="revenue-composition-label">
                  <span class="revenue-composition-color" style="background-color: #38A169;"></span>
                  <span class="revenue-composition-text indent">↳ New ARR Growth</span>
                </div>
                <div class="revenue-composition-value">€{{ formatNumber(revenueCompositionNewArrGrowth) }}</div>
                <div class="revenue-composition-percent">{{ revenueCompositionNewArrPercent }}%</div>
              </div>

              <div class="revenue-composition-item sub-item">
                <div class="revenue-composition-label">
                  <span class="revenue-composition-color" style="background-color: #68D391;"></span>
                  <span class="revenue-composition-text indent">↳ One-off revenue</span>
                </div>
                <div class="revenue-composition-value">€{{ formatNumber(revenueCompositionNonArr) }}</div>
                <div class="revenue-composition-percent">{{ revenueCompositionNonArrPercent }}%</div>
              </div>

              <div class="revenue-composition-divider"></div>

              <div class="revenue-composition-item total">
                <div class="revenue-composition-label">
                  <span class="revenue-composition-text">Total Revenue</span>
                </div>
                <div class="revenue-composition-value">€{{ formatNumber(revenueData.total) }}</div>
                <div class="revenue-composition-percent">100%</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Recent Revenue Card -->
    <div class="card recent-revenue-card">
      <div class="card-title-with-actions">
        <h3 class="card-title">Recent Revenue</h3>
        <button @click="fetchRecentRevenueData" class="refresh-button" :disabled="recentRevenueData.loading">
          <v-icon name="sync-alt" :class="{ 'spinning': recentRevenueData.loading }"></v-icon>
        </button>
      </div>

      <div class="recent-revenue-current-grid">
        <div class="recent-revenue-stat">
          <div class="recent-revenue-label">Today</div>
          <div class="recent-revenue-value">€{{ formatNumber(recentRevenueData.todayInvoiced) }}</div>
          <div class="recent-revenue-sublabel">invoiced</div>
          <div class="recent-revenue-comparison">
            <span class="recent-revenue-ly">{{ primaryYear - 1 }}: €{{ formatNumber(recentRevenueData.todayLastYear) }}</span>
            <span class="recent-revenue-change" :class="recentRevenueData.todayInvoiced >= recentRevenueData.todayLastYear ? 'positive' : 'negative'">
              {{ recentRevenueChangeSign(recentRevenueData.todayInvoiced, recentRevenueData.todayLastYear) }}{{ recentRevenueChangePct(recentRevenueData.todayInvoiced, recentRevenueData.todayLastYear) }}%
            </span>
          </div>
        </div>

        <div class="recent-revenue-stat">
          <div class="recent-revenue-label">This Week</div>
          <div class="recent-revenue-value">€{{ formatNumber(recentRevenueData.thisWeekInvoiced) }}</div>
          <div class="recent-revenue-sublabel">invoiced</div>
          <div class="recent-revenue-comparison">
            <span class="recent-revenue-ly">{{ primaryYear - 1 }}: €{{ formatNumber(recentRevenueData.thisWeekLastYear) }}</span>
            <span class="recent-revenue-change" :class="recentRevenueData.thisWeekInvoiced >= recentRevenueData.thisWeekLastYear ? 'positive' : 'negative'">
              {{ recentRevenueChangeSign(recentRevenueData.thisWeekInvoiced, recentRevenueData.thisWeekLastYear) }}{{ recentRevenueChangePct(recentRevenueData.thisWeekInvoiced, recentRevenueData.thisWeekLastYear) }}%
            </span>
          </div>
        </div>

        <div class="recent-revenue-stat">
          <div class="recent-revenue-label">This Month</div>
          <div class="recent-revenue-value">€{{ formatNumber(recentRevenueData.thisMonthInvoiced) }}</div>
          <div class="recent-revenue-sublabel">invoiced</div>
          <div class="recent-revenue-scheduled" v-if="recentRevenueData.thisMonthScheduled > 0">
            + €{{ formatNumber(recentRevenueData.thisMonthScheduled) }} scheduled
            <span class="recent-revenue-total">
              = €{{ formatNumber(recentRevenueData.thisMonthInvoiced + recentRevenueData.thisMonthScheduled) }}
              <span v-if="recentRevenueData.thisMonthLastYear > 0"
                class="recent-revenue-total-change"
                :class="(recentRevenueData.thisMonthInvoiced + recentRevenueData.thisMonthScheduled) >= recentRevenueData.thisMonthLastYear ? 'positive' : 'negative'">
                {{ recentRevenueChangeSign(recentRevenueData.thisMonthInvoiced + recentRevenueData.thisMonthScheduled, recentRevenueData.thisMonthLastYear) }}{{ recentRevenueChangePct(recentRevenueData.thisMonthInvoiced + recentRevenueData.thisMonthScheduled, recentRevenueData.thisMonthLastYear) }}%
              </span>
            </span>
          </div>
          <div class="recent-revenue-comparison">
            <span class="recent-revenue-ly">{{ primaryYear - 1 }}: €{{ formatNumber(recentRevenueData.thisMonthLastYear) }}</span>
            <span class="recent-revenue-change" :class="recentRevenueData.thisMonthInvoiced >= recentRevenueData.thisMonthLastYear ? 'positive' : 'negative'">
              {{ recentRevenueChangeSign(recentRevenueData.thisMonthInvoiced, recentRevenueData.thisMonthLastYear) }}{{ recentRevenueChangePct(recentRevenueData.thisMonthInvoiced, recentRevenueData.thisMonthLastYear) }}%
            </span>
          </div>
          <div class="recent-revenue-next-year" v-if="recentRevenueData.nextYearThisMonthScheduled > 0">
            {{ primaryYear + 1 }} sched: €{{ formatNumber(recentRevenueData.nextYearThisMonthScheduled) }}
          </div>
        </div>
      </div>

      <div class="recent-revenue-upcoming-section">
        <div class="recent-revenue-upcoming-stat">
          <div class="recent-revenue-upcoming-label">Tomorrow</div>
          <div class="recent-revenue-upcoming-value">€{{ formatNumber(recentRevenueData.tomorrowScheduled) }}</div>
          <div class="recent-revenue-upcoming-sublabel">scheduled</div>
        </div>
        <div class="recent-revenue-upcoming-stat">
          <div class="recent-revenue-upcoming-label">Next Week</div>
          <div class="recent-revenue-upcoming-value">€{{ formatNumber(recentRevenueData.nextWeekScheduled) }}</div>
          <div class="recent-revenue-upcoming-sublabel">scheduled</div>
        </div>
        <div class="recent-revenue-upcoming-stat">
          <div class="recent-revenue-upcoming-label">Next Month</div>
          <div class="recent-revenue-upcoming-value">€{{ formatNumber(recentRevenueData.nextMonthScheduled) }}</div>
          <div class="recent-revenue-upcoming-sublabel">scheduled</div>
        </div>
      </div>
    </div>

    <!-- Second Row: Credits + Invoice Items -->
    <div class="second-row-grid">
      <!-- Credits Card -->
      <div class="card credits-card">
        <div class="credits-header">
          <div class="card-title-with-actions">
            <h3 class="card-title">Credits</h3>
            <button @click="refreshCreditsData" class="refresh-button" :disabled="creditsData.loading">
              <v-icon name="sync-alt" :class="{ 'spinning': creditsData.loading }"></v-icon>
            </button>
          </div>
          <div class="credits-summary">
            <span class="credits-value">€{{ formatNumber(creditsData.total) }}</span>
            <span class="comparison-year-reference">{{ comparisonYear }}: €{{ formatNumber(creditsData.previousYearTotal) }}</span>
            <span class="credits-change" :class="creditsData.change >= 0 ? 'negative' : 'positive'">
              <i :class="creditsData.change >= 0 ? 'fas fa-arrow-up' : 'fas fa-arrow-down'"></i>
              {{ Math.abs(creditsData.change) }}%
            </span>
          </div>
        </div>

        <div class="credits-content">
          <!-- Monthly Credits Doughnut Chart -->
          <div class="credits-chart">
            <ulu-doughnut-chart
              :chart-data="creditsChartData"
              :options="creditsChartOptions"
            ></ulu-doughnut-chart>
          </div>

          <!-- Monthly Credits Table -->
          <div class="credits-data">
            <div class="credits-header-row">
              <div class="month-column">Month:</div>
              <div class="amount-column">Amount:</div>
            </div>
            <div
              v-for="(item, index) in creditsData.monthlyData"
              :key="index"
              class="credits-row"
            >
              <div class="month-label">
                <span
                  class="color-indicator"
                  :style="{ backgroundColor: getCreditsColor(index) }"
                ></span>
                {{ item.month }}
              </div>
              <div class="amount-value">€{{ formatNumber(item.value) }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Invoice Items Card -->
      <div class="card invoice-items-card">
        <div class="invoice-items-header">
          <div class="card-title-with-actions">
            <h3 class="card-title">Invoice Items by Type (YTD)</h3>
            <button @click="refreshInvoiceItemsData" class="refresh-button" :disabled="invoiceItemsData.loading">
              <v-icon name="sync-alt" :class="{ 'spinning': invoiceItemsData.loading }"></v-icon>
            </button>
          </div>
          <div class="invoice-items-summary">
            <div class="invoice-items-value">€{{ formatNumber(invoiceItemsData.total) }}</div>
          </div>
        </div>

        <div class="invoice-items-content">
          <!-- Invoice Items Doughnut Chart -->
          <div class="invoice-items-chart">
            <ulu-doughnut-chart :chart-data="invoiceItemsChartData"></ulu-doughnut-chart>
          </div>

          <!-- Invoice Items Table with Comparison -->
          <div class="invoice-items-data">
            <div class="invoice-items-header-row-comparison">
              <div class="type-column">Type:</div>
              <div class="year-column">{{ primaryYear }}:</div>
              <div class="year-column">{{ comparisonYear }}:</div>
              <div class="change-column">Change:</div>
            </div>
            <div
              v-for="(item, index) in invoiceItemsData.items"
              :key="index"
              class="invoice-items-row-comparison"
            >
              <div class="type-label">
                <span
                  class="color-indicator"
                  :style="{ backgroundColor: getInvoiceItemsColor(index) }"
                ></span>
                {{ item.itemType }}
              </div>
              <div class="amount-value">€{{ formatNumber(item.value) }}</div>
              <div class="amount-value">€{{ formatNumber(item.comparisonValue) }}</div>
              <div class="change-value" :class="item.change >= 0 ? 'positive' : 'negative'">
                {{ item.change >= 0 ? '+' : '' }}{{ item.change }}%
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Third Row: ARR Start Year-over-Year -->
    <div class="arr-section">
      <div class="section-title">
        <h3>ARR Start Year-over-Year</h3>
        <div class="section-actions">
          <button @click="refreshArrData" class="refresh-button" :disabled="arrStartData.loading || arrStartYoyData.loading">
            <v-icon name="sync-alt" :class="{ 'spinning': arrStartData.loading || arrStartYoyData.loading }"></v-icon>
          </button>
          <button @click="openArrDetailsModal" class="details-button">
            <i class="fas fa-chart-line"></i> Details
          </button>
        </div>
      </div>

      <div class="card arr-merged-card">
        <div class="arr-merged-container">
          <div class="arr-merged-table" style="grid-template-columns: 200px repeat(3, 1fr);">
            <div class="arr-merged-header" style="grid-template-columns: 200px repeat(3, 1fr);">
              <div class="header-metric"></div>
              <div class="header-year">{{ comparisonYear }}</div>
              <div class="header-year">{{ primaryYear }}</div>
              <div class="header-year">{{ primaryYear + 1 }}</div>
            </div>

            <div class="arr-merged-row start-row" style="grid-template-columns: 200px repeat(3, 1fr);">
              <div class="row-metric">ARR Start of Year</div>
              <div class="row-year-value">€{{ formatNumber(arrStartYoyData.previousYear) }}</div>
              <div class="row-year-value">€{{ formatNumber(arrStartYoyData.currentYear) }}</div>
              <div class="row-year-value">€{{ formatNumber(arrStartYoyData.nextYear) }}</div>
            </div>

            <div class="arr-merged-row growth-row" style="grid-template-columns: 200px repeat(3, 1fr);">
              <div class="row-metric">Growth</div>
              <div class="row-year-value"></div>
              <div class="row-year-value growth-value" :class="arrStartYoyData.changePreviousToCurrent >= 0 ? 'positive' : 'negative'">
                {{ arrStartYoyData.changePreviousToCurrent >= 0 ? '+' : '' }}€{{ formatNumber(Math.abs(arrStartYoyData.currentYear - arrStartYoyData.previousYear)) }}
                <span class="growth-percent">({{ arrStartYoyData.changePreviousToCurrent >= 0 ? '+' : '' }}{{ arrStartYoyData.changePreviousToCurrent }}%)</span>
              </div>
              <div class="row-year-value growth-value" :class="arrStartYoyData.changeCurrentToNext >= 0 ? 'positive' : 'negative'">
                {{ arrStartYoyData.changeCurrentToNext >= 0 ? '+' : '' }}€{{ formatNumber(Math.abs(arrStartYoyData.nextYear - arrStartYoyData.currentYear)) }}
                <span class="growth-percent">({{ arrStartYoyData.changeCurrentToNext >= 0 ? '+' : '' }}{{ arrStartYoyData.changeCurrentToNext }}%)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ARR Start with Churn Year-over-Year -->
    <div class="arr-section">
      <div class="section-title">
        <h3>ARR Start Year-over-Year (with Churn)</h3>
        <div class="section-actions">
          <button @click="fetchArrStartWithChurnData" class="refresh-button" :disabled="arrStartWithChurnData.loading">
            <v-icon name="sync-alt" :class="{ 'spinning': arrStartWithChurnData.loading }"></v-icon>
          </button>
          <button @click="openChurnPerCustomerModal(comparisonYear)" class="details-button">
            <i class="fas fa-users"></i> Churn {{ comparisonYear }}
          </button>
          <button @click="openChurnPerCustomerModal(primaryYear)" class="details-button">
            <i class="fas fa-users"></i> Churn {{ primaryYear }}
          </button>
          <button @click="openChurnPerCustomerModal(primaryYear + 1)" class="details-button">
            <i class="fas fa-users"></i> Churn {{ primaryYear + 1 }}
          </button>
        </div>
      </div>

      <div class="card arr-merged-card">
        <div class="arr-merged-container">
          <div class="arr-merged-table" style="grid-template-columns: 200px repeat(3, 1fr);">
            <div class="arr-merged-header" style="grid-template-columns: 200px repeat(3, 1fr);">
              <div class="header-metric"></div>
              <div class="header-year">{{ comparisonYear }}</div>
              <div class="header-year">{{ primaryYear }}</div>
              <div class="header-year">{{ primaryYear + 1 }}</div>
            </div>

            <div class="arr-merged-row start-row" style="grid-template-columns: 200px repeat(3, 1fr);">
              <div class="row-metric">ARR Start of Year</div>
              <div class="row-year-value">€{{ formatNumber(arrStartWithChurnData.previousYear.arrStart) }}</div>
              <div class="row-year-value">€{{ formatNumber(arrStartWithChurnData.currentYear.arrStart) }}</div>
              <div class="row-year-value">€{{ formatNumber(arrStartWithChurnData.nextYear.arrStart) }}</div>
            </div>

            <div class="arr-merged-row growth-row" style="grid-template-columns: 200px repeat(3, 1fr);">
              <div class="row-metric">Growth</div>
              <div class="row-year-value"></div>
              <div class="row-year-value growth-value" :class="(arrStartWithChurnData.currentYear.arrStart - arrStartWithChurnData.previousYear.arrStart) >= 0 ? 'positive' : 'negative'">
                {{ (arrStartWithChurnData.currentYear.arrStart - arrStartWithChurnData.previousYear.arrStart) >= 0 ? '+' : '' }}€{{ formatNumber(Math.abs(arrStartWithChurnData.currentYear.arrStart - arrStartWithChurnData.previousYear.arrStart)) }}
              </div>
              <div class="row-year-value growth-value" :class="(arrStartWithChurnData.nextYear.arrStart - arrStartWithChurnData.currentYear.arrStart) >= 0 ? 'positive' : 'negative'">
                {{ (arrStartWithChurnData.nextYear.arrStart - arrStartWithChurnData.currentYear.arrStart) >= 0 ? '+' : '' }}€{{ formatNumber(Math.abs(arrStartWithChurnData.nextYear.arrStart - arrStartWithChurnData.currentYear.arrStart)) }}
              </div>
            </div>

            <div class="arr-merged-row churn-row" style="grid-template-columns: 200px repeat(3, 1fr);">
              <div class="row-metric">Churn</div>
              <div class="row-year-value churn-value negative">€{{ formatNumber(arrStartWithChurnData.previousYear.churn) }}</div>
              <div class="row-year-value churn-value negative">€{{ formatNumber(arrStartWithChurnData.currentYear.churn) }}</div>
              <div class="row-year-value churn-value negative">€{{ formatNumber(arrStartWithChurnData.nextYear.churn) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Churn per Customer Modal -->
    <div v-if="churnPerCustomerModal.show" class="modal-overlay" @click="closeChurnPerCustomerModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Churn per Customer ({{ churnPerCustomerModal.year }})</h3>
          <button @click="closeChurnPerCustomerModal" class="close-button">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="churnPerCustomerModal.loading" class="loading-placeholder">
            <p>Loading...</p>
          </div>
          <div v-else-if="churnPerCustomerModal.customers.length === 0" class="no-data">
            <p>No churn data found for this year</p>
          </div>
          <div v-else class="breakdown-table-container">
            <table class="breakdown-table">
              <thead>
                <tr>
                  <th>Customer Name</th>
                  <th>Churn</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="customer in churnPerCustomerModal.customers" :key="customer.customer_id">
                  <td>{{ customer.company_name }}</td>
                  <td class="price-cell churn-value">€{{ formatNumber(customer.churn) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Monthly Credits Breakdown Modal -->
    <div v-if="selectedMonthDetails" class="modal-overlay" @click="closeMonthlyBreakdown">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Credit Invoices - {{ selectedMonthDetails.month }} {{ selectedMonthDetails.year }}</h3>
          <button @click="closeMonthlyBreakdown" class="close-button">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="monthlyBreakdownData.loading" class="loading-placeholder">
            <p>Loading...</p>
          </div>
          <div v-else-if="monthlyBreakdownData.items.length === 0" class="no-data">
            <p>No credit invoices found for this month</p>
          </div>
          <div v-else class="breakdown-table-container">
            <table class="breakdown-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Customer Name</th>
                  <th>Invoice No</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in monthlyBreakdownData.items" :key="item.id">
                  <td>{{ formatDate(item.date) }}</td>
                  <td>{{ item.customerName }}</td>
                  <td>{{ item.invoiceNo }}</td>
                  <td class="price-cell">€{{ formatNumber(item.price) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Revenue Composition Breakdown Modal -->
    <div v-if="revenueCompositionModal.show" class="modal-overlay" @click="closeRevenueCompositionModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ revenueCompositionModal.segmentLabel }} - Breakdown by Item Type ({{ primaryYear }})</h3>
          <div class="modal-header-actions">
            <button @click="exportRevenueCompositionToCSV" class="export-button">
              <i class="fas fa-file-csv"></i> Export to CSV
            </button>
            <button @click="closeRevenueCompositionModal" class="close-button">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <div class="modal-body">
          <div class="modal-summary">
            <span class="modal-summary-label">Total:</span>
            <span class="modal-summary-value">€{{ formatNumber(revenueCompositionModal.segmentValue) }}</span>
          </div>

          <div v-if="revenueCompositionModal.loading" class="loading-placeholder">
            <p>Loading...</p>
          </div>
          <div v-else-if="revenueCompositionModal.items.length === 0" class="no-data">
            <p>No items found for this segment</p>
          </div>
          <div v-else class="breakdown-table-container">
            <table class="breakdown-table hierarchical">
              <thead>
                <tr>
                  <th>Item Type / Item Name</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(typeGroup, typeIndex) in revenueCompositionModal.items">
                  <!-- Item Type Header Row (Clickable) -->
                  <tr :key="`type-${typeIndex}`" class="type-header-row clickable" @click="toggleItemType(typeGroup.itemType)">
                    <td class="type-header">
                      <span class="toggle-icon">{{ isItemTypeExpanded(typeGroup.itemType) ? '▼' : '►' }}</span>
                      <strong>{{ typeGroup.itemType }}</strong>
                    </td>
                    <td class="price-cell type-total"><strong>€{{ formatNumber(typeGroup.total) }}</strong></td>
                  </tr>
                  <!-- Item Name Rows (Collapsible) -->
                  <template v-if="isItemTypeExpanded(typeGroup.itemType)">
                    <tr v-for="(child, childIndex) in typeGroup.children" :key="`type-${typeIndex}-child-${childIndex}`" class="name-row">
                      <td class="name-cell">{{ child.itemName }}</td>
                      <td class="price-cell">€{{ formatNumber(child.value) }}</td>
                    </tr>
                  </template>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- ARR Section - Original (kept for comparison) -->
    <div class="arr-section" style="display: none;">
      <div class="section-title">
        <h3>Annual Recurring Revenue (ARR) - Original</h3>
      </div>

      <div class="arr-grid">
        <!-- ARR Start Comparison -->
        <div class="card arr-start-card">
          <h4 class="arr-card-title">ARR Start Comparison</h4>
          <div class="arr-comparison-container">
            <div class="arr-year-block primary">
              <div class="arr-year-label">{{ primaryYear }}</div>
              <div class="arr-value">€{{ formatNumber(arrStartData.currentYear) }}</div>
            </div>
            <div class="arr-change-indicator">
              <i :class="arrStartData.change >= 0 ? 'fas fa-arrow-up' : 'fas fa-arrow-down'"
                 :style="{ color: arrStartData.change >= 0 ? '#48bb78' : '#e53e3e' }"></i>
              <span :class="arrStartData.change >= 0 ? 'positive' : 'negative'">
                {{ arrStartData.change >= 0 ? '+' : '' }}{{ arrStartData.change }}%
              </span>
            </div>
            <div class="arr-year-block comparison">
              <div class="arr-year-label">{{ comparisonYear }}</div>
              <div class="arr-value">€{{ formatNumber(arrStartData.previousYear) }}</div>
            </div>
          </div>
        </div>

        <!-- ARR Growth Analysis -->
        <div class="card arr-growth-card">
          <h4 class="arr-card-title">ARR Growth Analysis</h4>
          <div class="arr-growth-container">
            <div class="growth-year-block">
              <div class="growth-year">{{ primaryYear }}</div>
              <div class="growth-calculation">
                <span>€{{ formatNumber(primaryYearRevenueData.total) }}</span>
                <span class="operator">−</span>
                <span>€{{ formatNumber(arrStartData.currentYear) }}</span>
                <span class="operator">=</span>
              </div>
              <div class="growth-result" :class="arrChangePrimary >= 0 ? 'positive' : 'negative'">
                {{ arrChangePrimary >= 0 ? '+' : '' }}€{{ formatNumber(Math.abs(arrChangePrimary)) }}
                <span class="percentage">({{ arrChangePercentagePrimary >= 0 ? '+' : '' }}{{ arrChangePercentagePrimary }}%)</span>
              </div>
            </div>
            <div class="growth-divider"></div>
            <div class="growth-year-block">
              <div class="growth-year">{{ comparisonYear }}</div>
              <div class="growth-calculation">
                <span>€{{ formatNumber(comparisonYearRevenueData.total) }}</span>
                <span class="operator">−</span>
                <span>€{{ formatNumber(arrStartData.previousYear) }}</span>
                <span class="operator">=</span>
              </div>
              <div class="growth-result" :class="arrChangeComparison >= 0 ? 'positive' : 'negative'">
                {{ arrChangeComparison >= 0 ? '+' : '' }}€{{ formatNumber(Math.abs(arrChangeComparison)) }}
                <span class="percentage">({{ arrChangePercentageComparison >= 0 ? '+' : '' }}{{ arrChangePercentageComparison }}%)</span>
              </div>
            </div>
          </div>
        </div>

        <!-- ARR Revenue Breakdown Comparison -->
        <div class="card arr-breakdown-card">
          <h4 class="arr-card-title">ARR Revenue Breakdown Comparison</h4>
          <div class="arr-breakdown-table">
            <div class="breakdown-table-header">
              <div class="header-label"></div>
              <div class="header-year">{{ primaryYear }}</div>
              <div class="header-year">{{ comparisonYear }}</div>
              <div class="header-change">Change</div>
            </div>

            <div class="breakdown-table-row">
              <div class="row-label">Invoiced:</div>
              <div class="row-value">€{{ formatNumber(primaryYearRevenueData.invoiced) }}</div>
              <div class="row-value">€{{ formatNumber(comparisonYearRevenueData.invoiced) }}</div>
              <div class="row-change" :class="invoicedChange >= 0 ? 'positive' : 'negative'">
                {{ invoicedChange >= 0 ? '+' : '' }}€{{ formatNumber(Math.abs(invoicedChange)) }} ({{ invoicedChangePercent >= 0 ? '+' : '' }}{{ invoicedChangePercent }}%)
              </div>
            </div>

            <div class="breakdown-table-row">
              <div class="row-label">Scheduled:</div>
              <div class="row-value">€{{ formatNumber(primaryYearRevenueData.scheduled) }}</div>
              <div class="row-value">€{{ formatNumber(comparisonYearRevenueData.scheduled) }}</div>
              <div class="row-change" :class="scheduledChange >= 0 ? 'positive' : 'negative'">
                {{ scheduledChange >= 0 ? '+' : '' }}€{{ formatNumber(Math.abs(scheduledChange)) }} ({{ scheduledChangePercent >= 0 ? '+' : '' }}{{ scheduledChangePercent }}%)
              </div>
            </div>

            <div class="breakdown-table-row total-row">
              <div class="row-label">Total:</div>
              <div class="row-value">€{{ formatNumber(primaryYearRevenueData.total) }}</div>
              <div class="row-value">€{{ formatNumber(comparisonYearRevenueData.total) }}</div>
              <div class="row-change" :class="totalChange >= 0 ? 'positive' : 'negative'">
                {{ totalChange >= 0 ? '+' : '' }}€{{ formatNumber(Math.abs(totalChange)) }} ({{ totalChangePercent >= 0 ? '+' : '' }}{{ totalChangePercent }}%)
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ARR Customer Analysis Section -->
    <div class="arr-analysis-section">
      <div class="section-title">
        <h3>ARR Customer Analysis</h3>
      </div>

      <div class="arr-analysis-grid">
        <!-- New Customers Added -->
        <div class="card arr-analysis-card" @click="onArrCategoryClick('new', 'invoice')">
          <div class="arr-analysis-header">
            <h4 class="arr-analysis-title">ARR Added from New Customers</h4>
            <i class="fas fa-user-plus arr-analysis-icon"></i>
          </div>
          <div class="arr-analysis-value positive">
            +€{{ formatNumber(arrChangesInvoiceData.totalNewAdded) }}
          </div>
          <div class="arr-analysis-scheduled">
            <span class="scheduled-label">Scheduled:</span>
            <span class="scheduled-amount">€{{ formatNumber(arrChangesInvoiceData.totalNewScheduled) }}</span>
          </div>
          <div class="arr-analysis-count">
            {{ arrChangesInvoiceData.newCustomersAdded.length }} customers
          </div>
        </div>

        <!-- ARR from Existing Customers -->
        <div class="card arr-analysis-card merged-card" @click="onArrCategoryClick('all-existing', 'invoice')">
          <div class="arr-analysis-header">
            <h4 class="arr-analysis-title">ARR from Existing Customers</h4>
            <i class="fas fa-users arr-analysis-icon"></i>
          </div>
          <div class="arr-analysis-value neutral">
            €{{ formatNumber(arrChangesInvoiceData.totalExistingArr) }}
          </div>
          <div class="arr-analysis-scheduled">
            <span class="scheduled-label">Scheduled:</span>
            <span class="scheduled-amount">€{{ formatNumber(arrChangesInvoiceData.totalExistingArrScheduled) }}</span>
          </div>
          <div class="arr-analysis-count">
            {{ arrChangesInvoiceData.existingCustomersAll.length }} customers
          </div>
        </div>

        <!-- ARR Changes from Existing Customers -->
        <div class="card arr-analysis-card merged-card">
          <div class="arr-analysis-header">
            <h4 class="arr-analysis-title">ARR Changes from Existing Customers</h4>
            <i class="fas fa-exchange-alt arr-analysis-icon"></i>
          </div>
          <div class="arr-analysis-subvalue">
            <span class="added-amount positive clickable-amount" @click="onArrCategoryClick('existing', 'invoice')">
              +€{{ formatNumber(arrChangesInvoiceData.totalExistingAdded) }}
            </span>
            <span class="growth-info-inline">({{ arrChangesInvoiceData.existingCustomersAdded.length }} grew)</span>
          </div>
          <div class="arr-analysis-subvalue" style="margin-top: 8px;">
            <span class="removed-amount negative clickable-amount" @click="onArrCategoryClick('removed', 'invoice')">
              -€{{ formatNumber(Math.abs(arrChangesInvoiceData.totalRemoved)) }}
            </span>
            <span class="growth-info-inline">({{ arrChangesInvoiceData.customersRemoved.length }} removed)</span>
          </div>
          <div class="arr-analysis-value" style="margin-top: 12px; border-top: 1px solid #e2e8f0; padding-top: 12px;">
            <span class="delta-label">Net Delta:</span>
            <span :class="(arrChangesInvoiceData.totalExistingAdded - arrChangesInvoiceData.totalRemoved) >= 0 ? 'delta-positive' : 'delta-negative'">
              {{ (arrChangesInvoiceData.totalExistingAdded - arrChangesInvoiceData.totalRemoved) >= 0 ? '+' : '' }}€{{ formatNumber(arrChangesInvoiceData.totalExistingAdded - arrChangesInvoiceData.totalRemoved) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- ARR Customer List Modal -->
    <div v-if="selectedArrCategory" class="modal-overlay" @click="closeArrCustomerList">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>
            <span v-if="selectedArrCategory === 'new'">ARR Added from New Customers</span>
            <span v-else-if="selectedArrCategory === 'existing'">ARR Added from Existing Customers</span>
            <span v-else-if="selectedArrCategory === 'removed'">ARR Removed (Churn)</span>
            <span v-else-if="selectedArrCategory === 'all-existing'">ARR from Existing Customers</span>
          </h3>
          <div class="modal-header-actions">
            <button @click="exportArrCustomersToExcel" class="export-button">
              <i class="fas fa-file-csv"></i> Export to CSV
            </button>
            <button @click="closeArrCustomerList" class="close-button">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <div class="modal-body">
          <div v-if="arrCustomerListData.loading" class="loading-placeholder">
            <p>Loading...</p>
          </div>
          <div v-else-if="arrCustomerListData.customers.length === 0" class="no-data">
            <p>No customers found in this category</p>
          </div>
          <div v-else class="breakdown-table-container">
            <table class="breakdown-table">
              <thead>
                <tr v-if="selectedArrCategory === 'new'">
                  <th @click="sortArrCustomers('name')" class="sortable">
                    Customer Name
                    <i v-if="arrCustomerListData.sortColumn === 'name'" :class="arrCustomerListData.sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'"></i>
                    <i v-else class="fas fa-sort"></i>
                  </th>
                  <th @click="sortArrCustomers('currentArr')" class="sortable">
                    {{ primaryYear }} ARR
                    <i v-if="arrCustomerListData.sortColumn === 'currentArr'" :class="arrCustomerListData.sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'"></i>
                    <i v-else class="fas fa-sort"></i>
                  </th>
                  <th @click="sortArrCustomers('currentScheduled')" class="sortable">
                    Scheduled
                    <i v-if="arrCustomerListData.sortColumn === 'currentScheduled'" :class="arrCustomerListData.sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'"></i>
                    <i v-else class="fas fa-sort"></i>
                  </th>
                  <th @click="sortArrCustomers('total')" class="sortable">
                    Total
                    <i v-if="arrCustomerListData.sortColumn === 'total'" :class="arrCustomerListData.sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'"></i>
                    <i v-else class="fas fa-sort"></i>
                  </th>
                </tr>
                <tr v-else>
                  <th @click="sortArrCustomers('name')" class="sortable">
                    Customer Name
                    <i v-if="arrCustomerListData.sortColumn === 'name'" :class="arrCustomerListData.sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'"></i>
                    <i v-else class="fas fa-sort"></i>
                  </th>
                  <th @click="sortArrCustomers('currentArr')" class="sortable">
                    {{ primaryYear }} ARR
                    <i v-if="arrCustomerListData.sortColumn === 'currentArr'" :class="arrCustomerListData.sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'"></i>
                    <i v-else class="fas fa-sort"></i>
                  </th>
                  <th @click="sortArrCustomers('currentScheduled')" class="sortable">
                    Scheduled
                    <i v-if="arrCustomerListData.sortColumn === 'currentScheduled'" :class="arrCustomerListData.sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'"></i>
                    <i v-else class="fas fa-sort"></i>
                  </th>
                  <th @click="sortArrCustomers('previousArr')" class="sortable">
                    {{ comparisonYear }} ARR
                    <i v-if="arrCustomerListData.sortColumn === 'previousArr'" :class="arrCustomerListData.sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'"></i>
                    <i v-else class="fas fa-sort"></i>
                  </th>
                  <th @click="sortArrCustomers('arrChange')" class="sortable">
                    Change
                    <i v-if="arrCustomerListData.sortColumn === 'arrChange'" :class="arrCustomerListData.sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'"></i>
                    <i v-else class="fas fa-sort"></i>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="customer in sortedArrCustomers" :key="customer.id">
                  <td>{{ customer.name }}</td>
                  <td>€{{ formatNumber(customer.currentArr) }}</td>
                  <td v-if="selectedArrCategory === 'new'">€{{ formatNumber(customer.currentScheduled) }}</td>
                  <td v-if="selectedArrCategory === 'new'">€{{ formatNumber(customer.total) }}</td>
                  <td v-if="selectedArrCategory !== 'new'">€{{ formatNumber(customer.currentScheduled) }}</td>
                  <td v-if="selectedArrCategory !== 'new'">€{{ formatNumber(customer.previousArr) }}</td>
                  <td v-if="selectedArrCategory !== 'new'" :class="customer.arrChange >= 0 ? 'arr-change-positive' : 'arr-change-negative'">
                    {{ customer.arrChange >= 0 ? '+' : '' }}€{{ formatNumber(customer.arrChange) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- ARR Details Modal -->
    <div v-if="showArrDetailsModal" class="modal-overlay" @click="closeArrDetailsModal">
      <div class="modal-content arr-details-modal" @click.stop>
        <div class="modal-header">
          <h3>Annual Recurring Revenue (ARR) - Detailed Breakdown</h3>
          <button @click="closeArrDetailsModal" class="close-button">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="card arr-merged-card">
            <div class="arr-merged-container">
              <!-- Table Section -->
              <div class="arr-merged-table">
                <!-- Table Header -->
                <div class="arr-merged-header">
                  <div class="header-metric"></div>
                  <div class="header-year">{{ primaryYear }}</div>
                  <div class="header-year">{{ comparisonYear }}</div>
                  <div class="header-change">Change</div>
                </div>

                <!-- ARR Start Row -->
                <div class="arr-merged-row start-row">
                  <div class="row-metric">ARR Start (Jan 1)</div>
                  <div class="row-year-value">€{{ formatNumber(arrStartData.currentYear) }}</div>
                  <div class="row-year-value">€{{ formatNumber(arrStartData.previousYear) }}</div>
                  <div class="row-change-value" :class="arrStartData.change >= 0 ? 'positive' : 'negative'">
                    {{ arrStartData.change >= 0 ? '+' : '' }}€{{ formatNumber(Math.abs(arrStartData.currentYear - arrStartData.previousYear)) }} ({{ arrStartData.change >= 0 ? '+' : '' }}{{ arrStartData.change }}%)
                  </div>
                </div>

                <!-- Invoiced Row -->
                <div class="arr-merged-row">
                  <div class="row-metric indent">+ Invoiced Revenue</div>
                  <div class="row-year-value">€{{ formatNumber(primaryYearRevenueData.invoiced) }}</div>
                  <div class="row-year-value">€{{ formatNumber(comparisonYearRevenueData.invoiced) }}</div>
                  <div class="row-change-value" :class="invoicedChange >= 0 ? 'positive' : 'negative'">
                    {{ invoicedChange >= 0 ? '+' : '' }}€{{ formatNumber(Math.abs(invoicedChange)) }} ({{ invoicedChangePercent >= 0 ? '+' : '' }}{{ invoicedChangePercent }}%)
                  </div>
                </div>

                <!-- Scheduled Row -->
                <div class="arr-merged-row">
                  <div class="row-metric indent">+ Scheduled Revenue</div>
                  <div class="row-year-value">€{{ formatNumber(primaryYearRevenueData.scheduled) }}</div>
                  <div class="row-year-value">€{{ formatNumber(comparisonYearRevenueData.scheduled) }}</div>
                  <div class="row-change-value" :class="scheduledChange >= 0 ? 'positive' : 'negative'">
                    {{ scheduledChange >= 0 ? '+' : '' }}€{{ formatNumber(Math.abs(scheduledChange)) }} ({{ scheduledChangePercent >= 0 ? '+' : '' }}{{ scheduledChangePercent }}%)
                  </div>
                </div>

                <!-- Credited Row -->
                <div class="arr-merged-row">
                  <div class="row-metric indent credit-label">- Credited Revenue</div>
                  <div class="row-year-value credit-value">€{{ formatNumber(primaryYearRevenueData.credited) }}</div>
                  <div class="row-year-value credit-value">€{{ formatNumber(comparisonYearRevenueData.credited) }}</div>
                  <div class="row-change-value credit-value" :class="creditedChange >= 0 ? 'positive' : 'negative'">
                    {{ creditedChange >= 0 ? '+' : '' }}€{{ formatNumber(Math.abs(creditedChange)) }} ({{ creditedChangePercent >= 0 ? '+' : '' }}{{ creditedChangePercent }}%)
                  </div>
                </div>

                <!-- ARR End Row -->
                <div class="arr-merged-row end-row">
                  <div class="row-metric">ARR End (Dec 31)</div>
                  <div class="row-year-value">€{{ formatNumber(primaryYearRevenueData.total) }}</div>
                  <div class="row-year-value">€{{ formatNumber(comparisonYearRevenueData.total) }}</div>
                  <div class="row-change-value" :class="totalChange >= 0 ? 'positive' : 'negative'">
                    {{ totalChange >= 0 ? '+' : '' }}€{{ formatNumber(Math.abs(totalChange)) }} ({{ totalChangePercent >= 0 ? '+' : '' }}{{ totalChangePercent }}%)
                  </div>
                </div>

                <!-- Growth Summary Row -->
                <div class="arr-merged-row growth-row">
                  <div class="row-metric">ARR Growth</div>
                  <div class="row-year-value growth-value" :class="arrChangePrimary >= 0 ? 'positive' : 'negative'">
                    {{ arrChangePrimary >= 0 ? '+' : '' }}€{{ formatNumber(Math.abs(arrChangePrimary)) }}
                    <span class="growth-percent">({{ arrChangePercentagePrimary >= 0 ? '+' : '' }}{{ arrChangePercentagePrimary }}%)</span>
                  </div>
                  <div class="row-year-value growth-value" :class="arrChangeComparison >= 0 ? 'positive' : 'negative'">
                    {{ arrChangeComparison >= 0 ? '+' : '' }}€{{ formatNumber(Math.abs(arrChangeComparison)) }}
                    <span class="growth-percent">({{ arrChangePercentageComparison >= 0 ? '+' : '' }}{{ arrChangePercentageComparison }}%)</span>
                  </div>
                  <div class="row-change-value"></div>
                </div>
              </div>

              <!-- Chart Section -->
              <div class="arr-merged-chart">
                <canvas ref="arrChartModal"></canvas>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Invoice Schedule Changes Modal -->
    <div v-if="scheduleChangesModal.show" class="modal-overlay" @click="closeScheduleChangesModal">
      <div class="modal-content large-modal" @click.stop>
        <div class="modal-header">
          <h3>Revenue Changes {{ primaryYear }}</h3>
          <button @click="closeScheduleChangesModal" class="close-button">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="scheduleChangesModal.loading" class="loading-placeholder">
            <p>Loading changes...</p>
          </div>
          <div v-else-if="scheduleChangesModal.changes.length === 0" class="no-data">
            <p>No schedule changes found for this period</p>
          </div>
          <div v-else class="schedule-changes-container">
            <div class="schedule-changes-list" @scroll="handleScheduleChangesScroll">
              <div
                v-for="change in scheduleChangesModal.changes"
                :key="`${change.order_id}-${change.schedule_year}`"
                class="schedule-change-item"
              >
                <div class="change-line-1">
                  <div class="change-customer">
                    <strong>{{ change.customer_name }}</strong>
                    <span class="order-number">Order #{{ change.order_number }}</span>
                  </div>
                  <div
                    class="change-revenue-impact"
                    :class="change.total_revenue_impact >= 0 ? 'positive' : 'negative'"
                  >
                    {{ change.total_revenue_impact >= 0 ? '+' : '' }}€{{ formatNumber(Math.abs(change.total_revenue_impact / 100)) }}
                  </div>
                </div>
                <div class="change-line-2">
                  <div class="change-meta">
                    <span class="meta-item">
                      <span class="meta-label">Months:</span>
                      <span class="meta-value">{{ change.schedules_affected }}</span>
                    </span>
                    <span class="meta-separator">•</span>
                    <span class="meta-item">
                      <span class="meta-label">Change:</span>
                      <span class="meta-value change-types">
                        <span
                          v-for="(type, index) in change.change_types"
                          :key="type"
                        >{{ formatChangeType(type) }}<span v-if="index < change.change_types.length - 1">, </span></span>
                      </span>
                    </span>
                    <span class="meta-separator">•</span>
                    <span class="meta-item">
                      <span class="meta-value">{{ formatDate(change.last_change_at) }}</span>
                    </span>
                  </div>
                </div>
              </div>
              <div v-if="scheduleChangesModal.hasMore && !scheduleChangesModal.loadingMore" class="load-more-trigger"></div>
              <div v-if="scheduleChangesModal.loadingMore" class="loading-more">
                <p>Loading more...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Chart from 'chart.js';

export default {
  name: "Overview",
  components: {
    "ulu-doughnut-chart": () => import("@/components/common/charts/DoughnutChart.vue")
  },
  data() {
    return {
      primaryYear: new Date().getFullYear(),
      comparisonYear: new Date().getFullYear() - 1,
      selectedBrand: '',
      availableBrands: [
        { value: 'ulu', label: 'ULU' },
        { value: 'ulubv', label: 'ULU BV' },
        { value: 'uluuk', label: 'ULU UK' },
        { value: 'cartracker', label: 'CarTracker' },
        { value: 'traksi', label: 'Traksi' },
        { value: 'imoto', label: 'iMoto' }
      ],
      revenueData: {
        invoiced: 0,
        scheduled: 0,
        total: 0,
        change: 0,
        loading: false
      },
      ytdComparison: {
        previousSamePeriod: 0,
        previousFullYear: 0,
        previousRemaining: 0,
        revenueChange: 0,
        scheduledChange: 0
      },
      creditsData: {
        total: 0,
        previousYearTotal: 0,
        change: 0,
        monthlyData: [],
        loading: false
      },
      arrStartData: {
        currentYear: 0,
        previousYear: 0,
        change: 0,
        loading: false
      },
      arrStartYoyData: {
        previousYear: 0,
        currentYear: 0,
        nextYear: 0,
        changePreviousToCurrent: 0,
        changeCurrentToNext: 0,
        loading: false
      },
      arrStartWithChurnData: {
        previousYear: { arrStart: 0, churn: 0 },
        currentYear: { arrStart: 0, churn: 0 },
        nextYear: { arrStart: 0, churn: 0 },
        loading: false
      },
      churnPerCustomerModal: {
        show: false,
        year: null,
        customers: [],
        loading: false
      },
      activeCustomersData: {
        currentYear: 0,
        previousYear: 0,
        change: 0,
        loading: false
      },
      primaryYearRevenueData: {
        invoiced: 0,
        scheduled: 0,
        credited: 0,
        total: 0,
        loading: false
      },
      comparisonYearRevenueData: {
        invoiced: 0,
        scheduled: 0,
        credited: 0,
        total: 0,
        loading: false
      },
      selectedMonthDetails: null,
      monthlyBreakdownData: {
        items: [],
        loading: false
      },
      showArrDetailsModal: false,
      invoiceItemsData: {
        items: [],
        total: 0,
        comparisonTotal: 0,
        loading: false
      },
      revenueCompositionModal: {
        show: false,
        segmentType: '',
        segmentLabel: '',
        segmentValue: 0,
        items: [],
        loading: false,
        expandedTypes: {}
      },
      arrChangesInvoiceData: {
        newCustomersAdded: [],
        existingCustomersAdded: [],
        customersRemoved: [],
        existingCustomersAll: [],
        totalNewAdded: 0,
        totalNewScheduled: 0,
        totalExistingAdded: 0,
        totalExistingScheduled: 0,
        totalRemoved: 0,
        totalRemovedScheduled: 0,
        totalExistingArr: 0,
        totalExistingArrScheduled: 0,
        loading: false
      },
      selectedArrCategory: null,
      selectedArrDataSource: null, // 'year_info' or 'invoice'
      arrCustomerListData: {
        customers: [],
        loading: false,
        sortColumn: null,
        sortDirection: 'desc' // 'asc' or 'desc'
      },
      scheduleChangesModal: {
        show: false,
        changes: [],
        loading: false,
        loadingMore: false,
        hasMore: true,
        offset: 0,
        limit: 20
      },
      arrChartInstance: null,
      arrChartModalInstance: null,
      recentRevenueData: {
        todayInvoiced: 0,
        thisWeekInvoiced: 0,
        thisMonthInvoiced: 0,
        tomorrowScheduled: 0,
        thisMonthScheduled: 0,
        todayLastYear: 0,
        thisWeekLastYear: 0,
        thisMonthLastYear: 0,
        nextYearThisMonthScheduled: 0,
        nextWeekScheduled: 0,
        nextMonthScheduled: 0,
        loading: false
      }
    };
  },
  computed: {
    ...mapGetters(["getUserName", "getUserSystem", "getGraphQlUrl", "getUserToken"]),
    availableYears() {
      const currentYear = new Date().getFullYear();
      const years = [];
      years.push(currentYear + 1);
      for (let i = 0; i < 5; i++) {
        years.push(currentYear - i);
      }
      return years;
    },
    sortedArrCustomers() {
      if (!this.arrCustomerListData.sortColumn) {
        return this.arrCustomerListData.customers;
      }

      const sorted = [...this.arrCustomerListData.customers].sort((a, b) => {
        let aValue = a[this.arrCustomerListData.sortColumn];
        let bValue = b[this.arrCustomerListData.sortColumn];

        // Handle string sorting (customer name)
        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (this.arrCustomerListData.sortDirection === 'asc') {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        }
      });

      return sorted;
    },
    creditsChartData() {
      const colors = [
        '#4A5568', '#48BB78', '#ED8936', '#A0AEC0', '#805AD5',
        '#E53E3E', '#38B2AC', '#D69E2E', '#3182CE', '#9F7AEA',
        '#DD6B20', '#319795'
      ];

      return {
        labels: this.creditsData.monthlyData.map(item => item.month),
        datasets: [
          {
            data: this.creditsData.monthlyData.map(item => item.value),
            backgroundColor: colors.slice(0, this.creditsData.monthlyData.length),
            borderWidth: 0
          }
        ]
      };
    },
    creditsChartOptions() {
      return {
        cutoutPercentage: 70,
        legend: {
          display: false
        },
        responsive: true,
        maintainAspectRatio: true,
        tooltips: {
          enabled: true
        },
        onClick: (event, elements) => {
          if (elements && elements.length > 0) {
            const clickedIndex = elements[0]._index;
            const clickedMonth = this.creditsData.monthlyData[clickedIndex];

            if (clickedMonth) {
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              const monthNumber = monthNames.indexOf(clickedMonth.month) + 1;

              if (monthNumber > 0) {
                this.onMonthClicked({
                  month: clickedMonth.month,
                  monthNumber: monthNumber,
                  value: clickedMonth.value,
                  year: this.primaryYear
                });
              }
            }
          }
        }
      };
    },
    arrChangePrimary() {
      return this.primaryYearRevenueData.total - this.arrStartData.currentYear;
    },
    arrChangePercentagePrimary() {
      return this.calculatePercentageChange(
        this.primaryYearRevenueData.total,
        this.arrStartData.currentYear
      );
    },
    arrChangeComparison() {
      return this.comparisonYearRevenueData.total - this.arrStartData.previousYear;
    },
    arrChangePercentageComparison() {
      return this.calculatePercentageChange(
        this.comparisonYearRevenueData.total,
        this.arrStartData.previousYear
      );
    },
    invoiceItemsChartData() {
      const colors = [
        '#4A5568', '#48BB78', '#ED8936', '#A0AEC0', '#805AD5',
        '#E53E3E', '#38B2AC', '#D69E2E', '#3182CE', '#9F7AEA',
        '#DD6B20', '#319795', '#F687B3', '#FC8181', '#F6AD55',
        '#68D391', '#4FD1C5', '#63B3ED', '#7F9CF5', '#B794F4',
        '#F56565', '#ED64A6', '#ECC94B', '#4299E1', '#667EEA',
        '#9AE6B4', '#81E6D9', '#90CDF4', '#C3DAFE', '#E9D8FD',
        '#FBB6CE', '#FED7D7'
      ];

      return {
        labels: this.invoiceItemsData.items.map(item => item.itemType),
        datasets: [
          {
            data: this.invoiceItemsData.items.map(item => item.value),
            backgroundColor: colors.slice(0, this.invoiceItemsData.items.length),
            borderWidth: 0
          }
        ]
      };
    },
    revenueCompositionNewRevenue() {
      return this.revenueData.total - this.arrStartData.currentYear;
    },
    revenueCompositionNewArrGrowth() {
      // ARR End - ARR Start = ARR growth during the year
      return this.primaryYearRevenueData.total - this.arrStartData.currentYear;
    },
    revenueCompositionNonArr() {
      // Total Revenue - ARR End = Non-recurring/one-time revenue
      return this.revenueData.total - this.primaryYearRevenueData.total;
    },
    revenueCompositionOldPercent() {
      if (this.revenueData.total === 0) return 0;
      return parseFloat(((this.arrStartData.currentYear / this.revenueData.total) * 100).toFixed(1));
    },
    revenueCompositionNewPercent() {
      if (this.revenueData.total === 0) return 0;
      return parseFloat(((this.revenueCompositionNewRevenue / this.revenueData.total) * 100).toFixed(1));
    },
    revenueCompositionNewArrPercent() {
      if (this.revenueData.total === 0) return 0;
      return parseFloat(((this.revenueCompositionNewArrGrowth / this.revenueData.total) * 100).toFixed(1));
    },
    revenueCompositionNonArrPercent() {
      if (this.revenueData.total === 0) return 0;
      return parseFloat(((this.revenueCompositionNonArr / this.revenueData.total) * 100).toFixed(1));
    },
    revenueCompositionChartData() {
      return {
        labels: ['ARR start of period', 'New ARR Growth', 'One-off revenue'],
        datasets: [
          // Outer ring - Main breakdown
          {
            data: [this.arrStartData.currentYear, this.revenueCompositionNewRevenue],
            backgroundColor: ['#FC8181', '#48BB78'],
            borderWidth: 0,
            weight: 1
          },
          // Inner ring - New Revenue breakdown
          {
            data: [
              this.arrStartData.currentYear, // Align with outer "ARR start of period"
              this.revenueCompositionNewArrGrowth, // New ARR portion
              this.revenueCompositionNonArr // One-off revenue portion
            ],
            backgroundColor: [
              'transparent', // Transparent for ARR start of period alignment
              '#38A169', // Darker green for New ARR
              '#68D391' // Lighter green for One-off revenue
            ],
            borderWidth: 0,
            weight: 0.7
          }
        ]
      };
    },
    revenueCompositionChartOptions() {
      return {
        cutoutPercentage: 60,
        legend: {
          display: false
        },
        responsive: true,
        maintainAspectRatio: true,
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) => {
              const datasetIndex = tooltipItem.datasetIndex;
              const index = tooltipItem.index;
              const value = data.datasets[datasetIndex].data[index];

              if (value === 0 || !value) return null;

              const total = this.revenueData.total;
              const percentage = ((value / total) * 100).toFixed(1);

              let label = '';
              if (datasetIndex === 0) {
                // Outer ring
                label = index === 0 ? 'ARR start of period' : 'New Revenue';
              } else {
                // Inner ring
                if (index === 0) return null; // Skip transparent section
                label = index === 1 ? 'New ARR Growth' : 'One-off revenue';
              }

              return `${label}: €${this.formatNumber(value)} (${percentage}%)`;
            }
          }
        },
        onClick: (event, elements) => {
          if (elements && elements.length > 0) {
            const clickedElement = elements[0];
            const datasetIndex = clickedElement._datasetIndex;
            const index = clickedElement._index;

            // Determine which segment was clicked
            let segmentType = '';
            if (datasetIndex === 0 && index === 0) {
              segmentType = 'old_revenue';
            } else if (datasetIndex === 1 && index === 1) {
              segmentType = 'new_arr';
            } else if (datasetIndex === 1 && index === 2) {
              segmentType = 'non_arr';
            }

            if (segmentType) {
              this.onRevenueCompositionSegmentClicked(segmentType);
            }
          }
        }
      };
    },
    invoicedChange() {
      return this.primaryYearRevenueData.invoiced - this.comparisonYearRevenueData.invoiced;
    },
    invoicedChangePercent() {
      return this.calculatePercentageChange(
        this.primaryYearRevenueData.invoiced,
        this.comparisonYearRevenueData.invoiced
      );
    },
    scheduledChange() {
      return this.primaryYearRevenueData.scheduled - this.comparisonYearRevenueData.scheduled;
    },
    scheduledChangePercent() {
      return this.calculatePercentageChange(
        this.primaryYearRevenueData.scheduled,
        this.comparisonYearRevenueData.scheduled
      );
    },
    creditedChange() {
      return this.primaryYearRevenueData.credited - this.comparisonYearRevenueData.credited;
    },
    creditedChangePercent() {
      return this.calculatePercentageChange(
        this.primaryYearRevenueData.credited,
        this.comparisonYearRevenueData.credited
      );
    },
    totalChange() {
      return this.primaryYearRevenueData.total - this.comparisonYearRevenueData.total;
    },
    totalChangePercent() {
      return this.calculatePercentageChange(
        this.primaryYearRevenueData.total,
        this.comparisonYearRevenueData.total
      );
    },
    arrComparisonChartData() {
      return {
        labels: ['ARR Start', 'Invoiced', 'Scheduled', 'ARR End'],
        datasets: [
          {
            label: this.primaryYear.toString(),
            data: [
              this.arrStartData.currentYear / 1000000,
              this.primaryYearRevenueData.invoiced / 1000000,
              this.primaryYearRevenueData.scheduled / 1000000,
              this.primaryYearRevenueData.total / 1000000
            ],
            backgroundColor: '#3182CE',
            borderWidth: 0
          },
          {
            label: this.comparisonYear.toString(),
            data: [
              this.arrStartData.previousYear / 1000000,
              this.comparisonYearRevenueData.invoiced / 1000000,
              this.comparisonYearRevenueData.scheduled / 1000000,
              this.comparisonYearRevenueData.total / 1000000
            ],
            backgroundColor: '#A0AEC0',
            borderWidth: 0
          }
        ]
      };
    },
    arrComparisonChartOptions() {
      return {
        scales: {
          xAxes: [{
            ticks: {
              beginAtZero: true,
              callback: (value) => '€' + value.toFixed(2) + 'M'
            },
            gridLines: {
              display: true
            }
          }],
          yAxes: [{
            gridLines: {
              display: false
            }
          }]
        },
        legend: {
          display: true,
          position: 'top'
        },
        responsive: true,
        maintainAspectRatio: false,
        tooltips: {
          mode: 'index',
          callbacks: {
            label: (tooltipItem, data) => {
              const label = data.datasets[tooltipItem.datasetIndex].label || '';
              const value = tooltipItem.xLabel;
              return label + ': €' + value.toFixed(2) + 'M';
            }
          }
        }
      };
    },
    revenueComparisonChartData() {
      return {
        labels: ['Total Revenue', 'YTD Revenue', 'Scheduled'],
        datasets: [
          {
            label: this.primaryYear.toString(),
            data: [
              this.revenueData.total / 1000000,
              this.revenueData.invoiced / 1000000,
              this.revenueData.scheduled / 1000000
            ],
            backgroundColor: '#3182CE',
            borderWidth: 0
          },
          {
            label: this.comparisonYear.toString(),
            data: [
              this.ytdComparison.previousFullYear / 1000000,
              this.ytdComparison.previousSamePeriod / 1000000,
              this.ytdComparison.previousRemaining / 1000000
            ],
            backgroundColor: '#A0AEC0',
            borderWidth: 0
          }
        ]
      };
    },
    revenueComparisonChartOptions() {
      return {
        scales: {
          xAxes: [{
            ticks: {
              beginAtZero: true,
              callback: (value) => '€' + value.toFixed(2) + 'M'
            },
            gridLines: {
              display: true
            }
          }],
          yAxes: [{
            gridLines: {
              display: false
            },
            barThickness: 40
          }]
        },
        legend: {
          display: true,
          position: 'top'
        },
        responsive: true,
        maintainAspectRatio: false,
        tooltips: {
          mode: 'index',
          callbacks: {
            label: (tooltipItem, data) => {
              const label = data.datasets[tooltipItem.datasetIndex].label || '';
              const value = tooltipItem.xLabel;
              return label + ': €' + value.toFixed(2) + 'M';
            }
          }
        }
      };
    }
  },
  methods: {
    ...mapActions("axios_mod", ["simpleFetch"]),

    formatNumber(num) {
      return Math.round(num).toLocaleString();
    },

    formatMillions(num) {
      return (num / 1000000).toFixed(2);
    },

    getCreditsColor(index) {
      const colors = [
        '#4A5568', '#48BB78', '#ED8936', '#A0AEC0', '#805AD5',
        '#E53E3E', '#38B2AC', '#D69E2E', '#3182CE', '#9F7AEA',
        '#DD6B20', '#319795'
      ];
      return colors[index] || '#4A5568';
    },

    getInvoiceItemsColor(index) {
      const colors = [
        '#4A5568', '#48BB78', '#ED8936', '#A0AEC0', '#805AD5',
        '#E53E3E', '#38B2AC', '#D69E2E', '#3182CE', '#9F7AEA',
        '#DD6B20', '#319795', '#F687B3', '#FC8181', '#F6AD55',
        '#68D391', '#4FD1C5', '#63B3ED', '#7F9CF5', '#B794F4',
        '#F56565', '#ED64A6', '#ECC94B', '#4299E1', '#667EEA',
        '#9AE6B4', '#81E6D9', '#90CDF4', '#C3DAFE', '#E9D8FD',
        '#FBB6CE', '#FED7D7'
      ];
      return colors[index] || '#4A5568';
    },

    async customFetch(data) {
      try {
        const response = await this.simpleFetch({
          data,
          this_ref: this,
          skip_confirm: true
        });
        return response;
      } catch (error) {
        console.error("Error fetching data:", error);
        return null;
      }
    },

    async onBrandChange() {
      await Promise.all([
        this.fetchRevenueToDate(),
        this.fetchRecentRevenueData(),
        this.fetchCreditsData(),
        this.fetchArrStartData(),
        this.fetchArrStartYoyData(),
        this.fetchArrStartWithChurnData(),
        this.fetchActiveCustomersData(),
        this.fetchPrimaryYearRevenueData(),
        this.fetchComparisonYearRevenueData(),
        this.fetchInvoiceItemsByType(),
        this.fetchArrChangesFromInvoices()
      ]);
    },

    async onYearSelectionChange() {
      await Promise.all([
        this.fetchRevenueToDate(),
        this.fetchRecentRevenueData(),
        this.fetchCreditsData(),
        this.fetchArrStartData(),
        this.fetchArrStartYoyData(),
        this.fetchArrStartWithChurnData(),
        this.fetchActiveCustomersData(),
        this.fetchPrimaryYearRevenueData(),
        this.fetchComparisonYearRevenueData(),
        this.fetchInvoiceItemsByType(),
        this.fetchArrChangesFromInvoices()
      ]);
    },

    async refreshRevenueData() {
      await Promise.all([
        this.fetchRevenueToDate(),
        this.fetchPrimaryYearRevenueData(),
        this.fetchComparisonYearRevenueData()
      ]);
    },

    async refreshRevenueCompositionData() {
      await Promise.all([
        this.fetchPrimaryYearRevenueData(),
        this.fetchComparisonYearRevenueData()
      ]);
    },

    async refreshCreditsData() {
      await this.fetchCreditsData();
    },

    async refreshInvoiceItemsData() {
      await this.fetchInvoiceItemsByType();
    },

    async refreshArrData() {
      // Fetch ARR start data for all years once, then populate both datasets
      this.arrStartData.loading = true;
      this.arrStartYoyData.loading = true;

      try {
        const previousYearData = {
          query: `
            query($target_year: Int!, $brand: String) {
              arrStartOfYear(target_year: $target_year, brand: $brand)
            }
          `,
          variables: {
            target_year: this.comparisonYear,
            brand: this.selectedBrand || "all"
          }
        };

        const currentYearData = {
          query: `
            query($target_year: Int!, $brand: String) {
              arrStartOfYear(target_year: $target_year, brand: $brand)
            }
          `,
          variables: {
            target_year: this.primaryYear,
            brand: this.selectedBrand || "all"
          }
        };

        const nextYearData = {
          query: `
            query($target_year: Int!, $brand: String) {
              arrStartOfYear(target_year: $target_year, brand: $brand)
            }
          `,
          variables: {
            target_year: this.primaryYear + 1,
            brand: this.selectedBrand || "all"
          }
        };

        const [previousResponse, currentResponse, nextResponse] = await Promise.all([
          this.customFetch(previousYearData),
          this.customFetch(currentYearData),
          this.customFetch(nextYearData),
          this.fetchArrChangesFromInvoices()
        ]);

        if (previousResponse && previousResponse.arrStartOfYear &&
            currentResponse && currentResponse.arrStartOfYear &&
            nextResponse && nextResponse.arrStartOfYear) {
          const previousData = JSON.parse(previousResponse.arrStartOfYear);
          const currentData = JSON.parse(currentResponse.arrStartOfYear);
          const nextData = JSON.parse(nextResponse.arrStartOfYear);

          const previousArr = previousData.arr_start_of_year || 0;
          const currentArr = currentData.arr_start_of_year || 0;
          const nextArr = nextData.arr_start_of_year || 0;

          // Populate arrStartData (comparison between current and previous)
          this.arrStartData.currentYear = currentArr / 100;
          this.arrStartData.previousYear = previousArr / 100;
          this.arrStartData.change = this.calculatePercentageChange(currentArr, previousArr);

          // Populate arrStartYoyData (year-over-year trends)
          this.arrStartYoyData.previousYear = previousArr / 100;
          this.arrStartYoyData.currentYear = currentArr / 100;
          this.arrStartYoyData.nextYear = nextArr / 100;
          this.arrStartYoyData.changePreviousToCurrent = this.calculatePercentageChange(currentArr, previousArr);
          this.arrStartYoyData.changeCurrentToNext = this.calculatePercentageChange(nextArr, currentArr);
        }
      } catch (error) {
        console.error("Error fetching ARR data:", error);
      } finally {
        this.arrStartData.loading = false;
        this.arrStartYoyData.loading = false;
      }
    },

    async fetchRevenueToDate() {
      this.revenueData.loading = true;
      try {
        const currentDate = new Date();
        const currentYear = this.primaryYear;
        const previousYear = this.comparisonYear;

        const currentCalendarYear = new Date().getFullYear();
        const startOfCurrentYear = new Date(currentCalendarYear, 0, 1);
        const dayOfYear = Math.floor((currentDate - startOfCurrentYear) / (1000 * 60 * 60 * 24)) + 1;

        const data = {
          query: `
            query($currentYear: Int!, $previousYear: Int!, $dayOfYear: Int!, $brand: String) {
              revenueToDate(currentYear: $currentYear, previousYear: $previousYear, dayOfYear: $dayOfYear, brand: $brand)
            }
          `,
          variables: {
            currentYear: currentYear,
            previousYear: previousYear,
            dayOfYear: dayOfYear,
            brand: this.selectedBrand || "all"
          }
        };

        const response = await this.customFetch(data);
        if (response && response.revenueToDate) {
          const [currentInvoiced, scheduledRemaining, previousFullYear, isCurrentYearFlag, previousSamePeriod] = response.revenueToDate;

          this.revenueData.invoiced = currentInvoiced / 100;
          this.revenueData.scheduled = scheduledRemaining / 100;
          this.revenueData.total = (currentInvoiced + scheduledRemaining) / 100;

          const totalRevenue = currentInvoiced + scheduledRemaining;
          this.revenueData.change = this.calculatePercentageChange(totalRevenue, previousFullYear);

          this.ytdComparison.previousSamePeriod = previousSamePeriod / 100;
          this.ytdComparison.previousFullYear = previousFullYear / 100;
          this.ytdComparison.previousRemaining = (previousFullYear - previousSamePeriod) / 100;
          this.ytdComparison.revenueChange = this.calculatePercentageChange(currentInvoiced, previousSamePeriod);
          this.ytdComparison.scheduledChange = this.calculatePercentageChange(scheduledRemaining, (previousFullYear - previousSamePeriod));
        }
      } catch (error) {
        console.error("Error fetching revenue data:", error);
      } finally {
        this.revenueData.loading = false;
      }
    },

    async fetchRecentRevenueData() {
      this.recentRevenueData.loading = true;
      try {
        const data = {
          query: `
            query($brand: String) {
              revenueRecent(brand: $brand)
            }
          `,
          variables: {
            brand: this.selectedBrand || "all"
          }
        };

        const response = await this.customFetch(data);
        if (response && response.revenueRecent) {
          const result = JSON.parse(response.revenueRecent);
          this.recentRevenueData.todayInvoiced = result.today_invoiced / 100;
          this.recentRevenueData.thisWeekInvoiced = result.this_week_invoiced / 100;
          this.recentRevenueData.thisMonthInvoiced = result.this_month_invoiced / 100;
          this.recentRevenueData.tomorrowScheduled = result.tomorrow_scheduled / 100;
          this.recentRevenueData.thisMonthScheduled = result.this_month_scheduled / 100;
          this.recentRevenueData.todayLastYear = result.today_last_year / 100;
          this.recentRevenueData.thisWeekLastYear = result.this_week_last_year / 100;
          this.recentRevenueData.thisMonthLastYear = result.this_month_last_year / 100;
          this.recentRevenueData.nextYearThisMonthScheduled = result.next_year_this_month_scheduled / 100;
          this.recentRevenueData.nextWeekScheduled = result.next_week_scheduled / 100;
          this.recentRevenueData.nextMonthScheduled = result.next_month_scheduled / 100;
        }
      } catch (error) {
        console.error("Error fetching recent revenue data:", error);
      } finally {
        this.recentRevenueData.loading = false;
      }
    },

    calculatePercentageChange(current, previous) {
      if (previous === 0) return current > 0 ? 100 : 0;
      return parseFloat(((current - previous) / previous * 100).toFixed(1));
    },

    recentRevenueChangePct(current, previous) {
      if (previous === 0) return current > 0 ? 100 : 0;
      return Math.abs(parseFloat(((current - previous) / previous * 100).toFixed(1)));
    },

    recentRevenueChangeSign(current, previous) {
      return current >= previous ? '+' : '-';
    },

    async fetchCreditsData() {
      this.creditsData.loading = true;
      try {
        const data = {
          query: `
            query($currentYear: Int!, $previousYear: Int!, $brand: String) {
              creditsToDate(currentYear: $currentYear, previousYear: $previousYear, brand: $brand)
            }
          `,
          variables: {
            currentYear: this.primaryYear,
            previousYear: this.comparisonYear,
            brand: this.selectedBrand || "all"
          }
        };

        const response = await this.customFetch(data);
        if (response && response.creditsToDate) {
          const creditsArray = response.creditsToDate;

          if (creditsArray.length >= 14) {
            const currentTotal = creditsArray[0];
            const previousTotal = creditsArray[1];
            const monthlyData = creditsArray.slice(2, 14);

            this.creditsData.total = Math.abs(currentTotal) / 100;
            this.creditsData.previousYearTotal = Math.abs(previousTotal) / 100;
            this.creditsData.change = this.calculatePercentageChange(Math.abs(currentTotal), Math.abs(previousTotal));

            this.creditsData.monthlyData = monthlyData.map((amount, index) => ({
              month: new Date(this.primaryYear, index, 1).toLocaleDateString('en-US', { month: 'short' }),
              value: Math.abs(amount) / 100,
              rawValue: amount
            })).filter(item => item.value > 0);
          }
        }
      } catch (error) {
        console.error("Error fetching credits data:", error);
      } finally {
        this.creditsData.loading = false;
      }
    },

    async fetchArrStartData() {
      this.arrStartData.loading = true;
      try {
        const currentYearData = {
          query: `
            query($target_year: Int!, $brand: String) {
              arrStartOfYear(target_year: $target_year, brand: $brand)
            }
          `,
          variables: {
            target_year: this.primaryYear,
            brand: this.selectedBrand || "all"
          }
        };

        const previousYearData = {
          query: `
            query($target_year: Int!, $brand: String) {
              arrStartOfYear(target_year: $target_year, brand: $brand)
            }
          `,
          variables: {
            target_year: this.comparisonYear,
            brand: this.selectedBrand || "all"
          }
        };

        const [currentResponse, previousResponse] = await Promise.all([
          this.customFetch(currentYearData),
          this.customFetch(previousYearData)
        ]);

        if (currentResponse && currentResponse.arrStartOfYear &&
            previousResponse && previousResponse.arrStartOfYear) {
          const currentData = JSON.parse(currentResponse.arrStartOfYear);
          const previousData = JSON.parse(previousResponse.arrStartOfYear);

          const currentArr = currentData.arr_start_of_year || 0;
          const previousArr = previousData.arr_start_of_year || 0;

          this.arrStartData.currentYear = currentArr / 100;
          this.arrStartData.previousYear = previousArr / 100;
          this.arrStartData.change = this.calculatePercentageChange(currentArr, previousArr);
        }
      } catch (error) {
        console.error("Error fetching ARR start data:", error);
      } finally {
        this.arrStartData.loading = false;
      }
    },

    async fetchArrStartYoyData() {
      this.arrStartYoyData.loading = true;
      try {
        const brandParam = this.selectedBrand || "all";

        const previousYearData = {
          query: `
            query($target_year: Int!, $brand: String) {
              arrStartOfYear(target_year: $target_year, brand: $brand)
            }
          `,
          variables: {
            target_year: this.comparisonYear,
            brand: brandParam
          }
        };

        const currentYearData = {
          query: `
            query($target_year: Int!, $brand: String) {
              arrStartOfYear(target_year: $target_year, brand: $brand)
            }
          `,
          variables: {
            target_year: this.primaryYear,
            brand: brandParam
          }
        };

        const nextYearData = {
          query: `
            query($target_year: Int!, $brand: String) {
              arrStartOfYear(target_year: $target_year, brand: $brand)
            }
          `,
          variables: {
            target_year: this.primaryYear + 1,
            brand: brandParam
          }
        };

        const [previousResponse, currentResponse, nextResponse] = await Promise.all([
          this.customFetch(previousYearData),
          this.customFetch(currentYearData),
          this.customFetch(nextYearData)
        ]);

        // Parse each response independently
        let previousArr = 0;
        let currentArr = 0;
        let nextArr = 0;

        if (previousResponse && previousResponse.arrStartOfYear) {
          try {
            const previousData = JSON.parse(previousResponse.arrStartOfYear);
            previousArr = previousData.arr_start_of_year || 0;
          } catch (e) {
            console.error("Error parsing previousYear ARR data:", e);
          }
        }

        if (currentResponse && currentResponse.arrStartOfYear) {
          try {
            const currentData = JSON.parse(currentResponse.arrStartOfYear);
            currentArr = currentData.arr_start_of_year || 0;
          } catch (e) {
            console.error("Error parsing currentYear ARR data:", e);
          }
        }

        if (nextResponse && nextResponse.arrStartOfYear) {
          try {
            const nextData = JSON.parse(nextResponse.arrStartOfYear);
            nextArr = nextData.arr_start_of_year || 0;
          } catch (e) {
            console.error("Error parsing nextYear ARR data:", e);
          }
        }

        // Update the data regardless of whether all responses succeeded
        this.arrStartYoyData.previousYear = previousArr / 100;
        this.arrStartYoyData.currentYear = currentArr / 100;
        this.arrStartYoyData.nextYear = nextArr / 100;
        this.arrStartYoyData.changePreviousToCurrent = this.calculatePercentageChange(currentArr, previousArr);
        this.arrStartYoyData.changeCurrentToNext = this.calculatePercentageChange(nextArr, currentArr);
      } catch (error) {
        console.error("Error fetching ARR start YoY data:", error);
      } finally {
        this.arrStartYoyData.loading = false;
      }
    },

    async fetchArrStartWithChurnData() {
      this.arrStartWithChurnData.loading = true;
      try {
        const brandParam = this.selectedBrand || "all";

        const fetchYear = (year) => ({
          query: `
            query($target_year: Int!, $brand: String) {
              arrStartOfYearWithChurn(target_year: $target_year, brand: $brand)
            }
          `,
          variables: {
            target_year: year,
            brand: brandParam
          }
        });

        const [previousResponse, currentResponse, nextResponse] = await Promise.all([
          this.customFetch(fetchYear(this.comparisonYear)),
          this.customFetch(fetchYear(this.primaryYear)),
          this.customFetch(fetchYear(this.primaryYear + 1))
        ]);

        const parseResponse = (response) => {
          if (response && response.arrStartOfYearWithChurn) {
            try {
              const data = JSON.parse(response.arrStartOfYearWithChurn);
              return {
                arrStart: (data.arr_start_of_year || 0) / 100,
                churn: (data.churn || 0) / 100
              };
            } catch (e) {
              console.error("Error parsing arrStartOfYearWithChurn data:", e);
            }
          }
          return { arrStart: 0, churn: 0 };
        };

        this.arrStartWithChurnData.previousYear = parseResponse(previousResponse);
        this.arrStartWithChurnData.currentYear = parseResponse(currentResponse);
        this.arrStartWithChurnData.nextYear = parseResponse(nextResponse);
      } catch (error) {
        console.error("Error fetching ARR start with churn data:", error);
      } finally {
        this.arrStartWithChurnData.loading = false;
      }
    },

    async fetchChurnPerCustomer(year) {
      this.churnPerCustomerModal.loading = true;
      try {
        const data = {
          query: `
            query($target_year: Int!, $brand: String) {
              churnPerCustomer(target_year: $target_year, brand: $brand)
            }
          `,
          variables: {
            target_year: year,
            brand: this.selectedBrand || "all"
          }
        };

        const response = await this.customFetch(data);
        if (response && response.churnPerCustomer) {
          const parsedData = JSON.parse(response.churnPerCustomer);
          this.churnPerCustomerModal.customers = (parsedData.customers || []).map(c => ({
            customer_id: c.customer_id,
            company_name: c.company_name,
            churn: c.churn / 100
          }));
        }
      } catch (error) {
        console.error("Error fetching churn per customer:", error);
      } finally {
        this.churnPerCustomerModal.loading = false;
      }
    },

    async openChurnPerCustomerModal(year) {
      this.churnPerCustomerModal.show = true;
      this.churnPerCustomerModal.year = year;
      this.churnPerCustomerModal.customers = [];
      await this.fetchChurnPerCustomer(year);
    },

    closeChurnPerCustomerModal() {
      this.churnPerCustomerModal.show = false;
      this.churnPerCustomerModal.customers = [];
    },

    async fetchActiveCustomersData() {
      this.activeCustomersData.loading = true;
      try {
        const data = {
          query: `
            query($currentYear: Int!, $previousYear: Int!, $brand: String) {
              activeCustomersCount(currentYear: $currentYear, previousYear: $previousYear, brand: $brand)
            }
          `,
          variables: {
            currentYear: this.primaryYear,
            previousYear: this.comparisonYear,
            brand: this.selectedBrand || "all"
          }
        };

        const response = await this.customFetch(data);

        if (response && response.activeCustomersCount) {
          const [currentYearCount, previousYearCount] = response.activeCustomersCount;

          this.activeCustomersData.currentYear = currentYearCount;
          this.activeCustomersData.previousYear = previousYearCount;
          this.activeCustomersData.change = this.calculatePercentageChange(currentYearCount, previousYearCount);
        }
      } catch (error) {
        console.error("Error fetching active customers data:", error);
      } finally {
        this.activeCustomersData.loading = false;
      }
    },

    async fetchPrimaryYearRevenueData() {
      this.primaryYearRevenueData.loading = true;
      try {
        const data = {
          query: `
            query($target_year: Int!, $brand: String) {
              yearRevenueBreakdown(target_year: $target_year, brand: $brand)
            }
          `,
          variables: {
            target_year: this.primaryYear,
            brand: this.selectedBrand || "all"
          }
        };

        const response = await this.customFetch(data);

        if (response && response.yearRevenueBreakdown) {
          const parsedData = JSON.parse(response.yearRevenueBreakdown);

          this.primaryYearRevenueData.invoiced = parsedData.invoiced_revenue / 100;
          this.primaryYearRevenueData.scheduled = parsedData.scheduled_revenue / 100;
          this.primaryYearRevenueData.credited = parsedData.credited_revenue / 100;
          this.primaryYearRevenueData.total = parsedData.total_revenue / 100;
        }
      } catch (error) {
        console.error("Error fetching primary year revenue data:", error);
      } finally {
        this.primaryYearRevenueData.loading = false;
      }
    },

    async fetchComparisonYearRevenueData() {
      this.comparisonYearRevenueData.loading = true;
      try {
        const data = {
          query: `
            query($target_year: Int!, $brand: String) {
              yearRevenueBreakdown(target_year: $target_year, brand: $brand)
            }
          `,
          variables: {
            target_year: this.comparisonYear,
            brand: this.selectedBrand || "all"
          }
        };

        const response = await this.customFetch(data);

        if (response && response.yearRevenueBreakdown) {
          const parsedData = JSON.parse(response.yearRevenueBreakdown);

          this.comparisonYearRevenueData.invoiced = parsedData.invoiced_revenue / 100;
          this.comparisonYearRevenueData.scheduled = parsedData.scheduled_revenue / 100;
          this.comparisonYearRevenueData.credited = parsedData.credited_revenue / 100;
          this.comparisonYearRevenueData.total = parsedData.total_revenue / 100;
        }
      } catch (error) {
        console.error("Error fetching comparison year revenue data:", error);
      } finally {
        this.comparisonYearRevenueData.loading = false;
      }
    },

    async onMonthClicked(monthDetails) {
      this.selectedMonthDetails = monthDetails;
      await this.fetchMonthlyBreakdown(monthDetails.year, monthDetails.monthNumber);
    },

    closeMonthlyBreakdown() {
      this.selectedMonthDetails = null;
      this.monthlyBreakdownData.items = [];
    },

    async onRevenueCompositionSegmentClicked(segmentType) {
      // Determine segment label and value
      let label = '';
      let value = 0;

      if (segmentType === 'old_revenue') {
        label = 'ARR start of period';
        value = this.arrStartData.currentYear;
      } else if (segmentType === 'new_arr') {
        label = 'New ARR Growth';
        value = this.revenueCompositionNewArrGrowth;
      } else if (segmentType === 'non_arr') {
        label = 'One-off revenue';
        value = this.revenueCompositionNonArr;
      }

      this.revenueCompositionModal.show = true;
      this.revenueCompositionModal.segmentType = segmentType;
      this.revenueCompositionModal.segmentLabel = label;
      this.revenueCompositionModal.segmentValue = value;

      await this.fetchRevenueCompositionBreakdown(segmentType);
    },

    closeRevenueCompositionModal() {
      this.revenueCompositionModal.show = false;
      this.revenueCompositionModal.items = [];
      this.revenueCompositionModal.segmentType = '';
      this.revenueCompositionModal.segmentLabel = '';
      this.revenueCompositionModal.segmentValue = 0;
      this.revenueCompositionModal.expandedTypes = {};
    },

    toggleItemType(itemType) {
      if (this.revenueCompositionModal.expandedTypes[itemType]) {
        this.$delete(this.revenueCompositionModal.expandedTypes, itemType);
      } else {
        this.$set(this.revenueCompositionModal.expandedTypes, itemType, true);
      }
    },

    isItemTypeExpanded(itemType) {
      return this.revenueCompositionModal.expandedTypes[itemType] || false;
    },

    exportRevenueCompositionToCSV() {
      if (!this.revenueCompositionModal.items || this.revenueCompositionModal.items.length === 0) {
        alert('No data to export');
        return;
      }

      // Create filename based on segment type
      const segmentLabel = this.revenueCompositionModal.segmentLabel.toLowerCase().replace(/\s+/g, '_');
      const filename = `revenue_composition_${segmentLabel}_${this.primaryYear}.csv`;

      // Create CSV content with hierarchical structure
      let csvContent = 'Item Type,Item Name,Value (cents),Value (euros)\n';

      // Add rows hierarchically
      this.revenueCompositionModal.items.forEach(typeGroup => {
        const itemType = '"' + (typeGroup.itemType || 'Unknown').replace(/"/g, '""') + '"';
        const typeTotalCents = Math.round(typeGroup.total * 100);
        const typeTotalEuros = typeGroup.total.toFixed(2);

        // Add item type header row
        csvContent += `${itemType},"[TOTAL]",${typeTotalCents},${typeTotalEuros}\n`;

        // Add each item name under this type
        typeGroup.children.forEach(child => {
          const itemName = '"' + (child.itemName || 'Unknown').replace(/"/g, '""') + '"';
          const valueCents = Math.round(child.value * 100);
          const valueEuros = child.value.toFixed(2);
          csvContent += `${itemType},${itemName},${valueCents},${valueEuros}\n`;
        });
      });

      // Add grand total row
      const totalCents = Math.round(this.revenueCompositionModal.segmentValue * 100);
      const totalEuros = this.revenueCompositionModal.segmentValue.toFixed(2);
      csvContent += `"GRAND TOTAL","",${totalCents},${totalEuros}\n`;

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },

    async fetchRevenueCompositionBreakdown(segmentType) {
      this.revenueCompositionModal.loading = true;
      try {
        const data = {
          query: `
            query($year: Int!, $brand: String, $segmentType: String!) {
              revenueCompositionBreakdownDetailed(year: $year, brand: $brand, segmentType: $segmentType)
            }
          `,
          variables: {
            year: this.primaryYear,
            brand: this.selectedBrand || 'all',
            segmentType: segmentType
          }
        };

        const response = await this.customFetch(data);
        if (response && response.revenueCompositionBreakdownDetailed) {
          const breakdownData = JSON.parse(response.revenueCompositionBreakdownDetailed);

          // Group by item_type and create hierarchical structure
          const grouped = {};
          breakdownData.forEach(item => {
            const type = item.itemType || 'Unknown';
            if (!grouped[type]) {
              grouped[type] = {
                itemType: type,
                total: 0,
                children: []
              };
            }
            const valueInEuros = item.value / 100;
            grouped[type].total += valueInEuros;
            grouped[type].children.push({
              itemName: item.itemName || 'Unknown',
              value: valueInEuros
            });
          });

          // Convert to array and sort
          this.revenueCompositionModal.items = Object.values(grouped).sort((a, b) => b.total - a.total);

          // Initialize all types as collapsed by default
          this.revenueCompositionModal.expandedTypes = {};
        }
      } catch (error) {
        console.error('Error fetching revenue composition breakdown:', error);
        this.revenueCompositionModal.items = [];
      } finally {
        this.revenueCompositionModal.loading = false;
      }
    },

    async fetchMonthlyBreakdown(year, month) {
      this.monthlyBreakdownData.loading = true;
      try {
        const data = {
          query: `
            query($year: Int!, $month: Int!) {
              monthlyCreditsBreakdown(year: $year, month: $month)
            }
          `,
          variables: {
            year: year,
            month: month
          }
        };

        const response = await this.customFetch(data);
        if (response && response.monthlyCreditsBreakdown) {
          const breakdownData = JSON.parse(response.monthlyCreditsBreakdown);
          this.monthlyBreakdownData.items = breakdownData.map(item => ({
            id: item.id,
            date: item.date,
            customerName: item.customer_name,
            invoiceNo: item.invoice_no,
            price: Math.abs(item.price) / 100
          })).sort((a, b) => b.price - a.price); // Sort by price descending
        }
      } catch (error) {
        console.error("Error fetching monthly breakdown:", error);
      } finally {
        this.monthlyBreakdownData.loading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    },

    calculateExistingGrowthPercentage(data) {
      if (data.totalExistingArr === 0) return 0;
      const previousTotal = data.totalExistingArr - data.totalExistingAdded;
      if (previousTotal === 0) return 100;
      return ((data.totalExistingAdded / previousTotal) * 100).toFixed(1);
    },

    async fetchInvoiceItemsByType() {
      this.invoiceItemsData.loading = true;
      try {
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1; // JavaScript months are 0-indexed

        const primaryYearData = {
          query: `
            query($year: Int!, $brand: String, $month: Int) {
              invoiceItemsByType(year: $year, brand: $brand, month: $month)
            }
          `,
          variables: {
            year: this.primaryYear,
            brand: this.selectedBrand || "all",
            month: currentMonth
          }
        };

        const comparisonYearData = {
          query: `
            query($year: Int!, $brand: String, $month: Int) {
              invoiceItemsByType(year: $year, brand: $brand, month: $month)
            }
          `,
          variables: {
            year: this.comparisonYear,
            brand: this.selectedBrand || "all",
            month: currentMonth
          }
        };

        const [primaryResponse, comparisonResponse] = await Promise.all([
          this.customFetch(primaryYearData),
          this.customFetch(comparisonYearData)
        ]);

        if (primaryResponse && primaryResponse.invoiceItemsByType) {
          const primaryItems = JSON.parse(primaryResponse.invoiceItemsByType);
          const comparisonItems = comparisonResponse && comparisonResponse.invoiceItemsByType
            ? JSON.parse(comparisonResponse.invoiceItemsByType)
            : [];

          // Create a map of comparison items for quick lookup
          const comparisonMap = {};
          comparisonItems.forEach(item => {
            comparisonMap[item.item_type] = parseFloat(item.total_price_ex_vat_sum) / 100;
          });

          // Merge primary and comparison data
          this.invoiceItemsData.items = primaryItems.map(item => {
            const itemType = item.item_type || 'Unknown';
            const primaryValue = parseFloat(item.total_price_ex_vat_sum) / 100;
            const comparisonValue = comparisonMap[item.item_type] || 0;

            return {
              itemType,
              value: primaryValue,
              comparisonValue,
              change: comparisonValue > 0 ? ((primaryValue - comparisonValue) / comparisonValue * 100).toFixed(1) : 0
            };
          }).filter(item => item.value !== 0);

          // Add any items from comparison year that don't exist in primary year
          comparisonItems.forEach(item => {
            const itemType = item.item_type || 'Unknown';
            if (!primaryItems.find(pi => pi.item_type === itemType)) {
              const comparisonValue = parseFloat(item.total_price_ex_vat_sum) / 100;
              this.invoiceItemsData.items.push({
                itemType,
                value: 0,
                comparisonValue,
                change: -100
              });
            }
          });

          this.invoiceItemsData.total = this.invoiceItemsData.items.reduce((sum, item) => sum + item.value, 0);
          this.invoiceItemsData.comparisonTotal = this.invoiceItemsData.items.reduce((sum, item) => sum + item.comparisonValue, 0);
        }
      } catch (error) {
        console.error("Error fetching invoice items by type:", error);
      } finally {
        this.invoiceItemsData.loading = false;
      }
    },

    async fetchArrChangesFromInvoices() {
      this.arrChangesInvoiceData.loading = true;
      try {
        const data = {
          query: `
            query($currentYear: Int!, $previousYear: Int!, $brand: String) {
              arrChangesByCustomerTypeFromInvoices(currentYear: $currentYear, previousYear: $previousYear, brand: $brand)
            }
          `,
          variables: {
            currentYear: this.primaryYear,
            previousYear: this.comparisonYear,
            brand: this.selectedBrand || "all"
          }
        };

        const response = await this.customFetch(data);
        if (response && response.arrChangesByCustomerTypeFromInvoices) {
          const parsedData = JSON.parse(response.arrChangesByCustomerTypeFromInvoices);

          this.arrChangesInvoiceData.newCustomersAdded = parsedData.new_customers_added || [];
          this.arrChangesInvoiceData.existingCustomersAdded = parsedData.existing_customers_added || [];
          this.arrChangesInvoiceData.customersRemoved = parsedData.customers_removed || [];
          this.arrChangesInvoiceData.existingCustomersAll = parsedData.existing_customers_all || [];
          this.arrChangesInvoiceData.totalNewAdded = parsedData.total_new_added / 100;
          this.arrChangesInvoiceData.totalNewScheduled = parsedData.total_new_scheduled / 100;
          this.arrChangesInvoiceData.totalExistingAdded = parsedData.total_existing_added / 100;
          this.arrChangesInvoiceData.totalExistingScheduled = parsedData.total_existing_scheduled / 100;
          this.arrChangesInvoiceData.totalRemoved = parsedData.total_removed / 100;
          this.arrChangesInvoiceData.totalRemovedScheduled = parsedData.total_removed_scheduled / 100;
          this.arrChangesInvoiceData.totalExistingArr = parsedData.total_existing_arr / 100;
          this.arrChangesInvoiceData.totalExistingArrScheduled = parsedData.total_existing_arr_scheduled / 100;
        }
      } catch (error) {
        console.error("Error fetching ARR changes from invoices:", error);
      } finally {
        this.arrChangesInvoiceData.loading = false;
      }
    },

    onArrCategoryClick(category, dataSource = 'year_info') {
      this.selectedArrCategory = category;
      this.selectedArrDataSource = dataSource;

      const sourceData = dataSource === 'invoice' ? this.arrChangesInvoiceData : this.arrChangesData;

      let customers = [];
      switch(category) {
        case 'new':
          customers = sourceData.newCustomersAdded;
          break;
        case 'existing':
          customers = sourceData.existingCustomersAdded;
          break;
        case 'removed':
          customers = sourceData.customersRemoved;
          break;
        case 'all-existing':
          customers = sourceData.existingCustomersAll;
          break;
      }

      this.arrCustomerListData.customers = customers
        .map(customer => ({
          id: customer.customer_id,
          name: customer.company_name,
          currentArr: customer.current_arr / 100,
          previousArr: customer.previous_arr / 100,
          arrChange: customer.arr_change / 100,
          currentScheduled: customer.current_scheduled ? customer.current_scheduled / 100 : 0,
          total: (customer.current_arr / 100) + (customer.current_scheduled ? customer.current_scheduled / 100 : 0),
          // Keep raw cent values for export
          currentArrCents: customer.current_arr,
          previousArrCents: customer.previous_arr,
          arrChangeCents: customer.arr_change,
          currentScheduledCents: customer.current_scheduled || 0,
          totalCents: customer.current_arr + (customer.current_scheduled || 0)
        }))
        .sort((a, b) => {
          // For all-existing, sort by current ARR (highest to lowest)
          if (category === 'all-existing') {
            return b.currentArr - a.currentArr;
          }
          // For other categories, sort by absolute change (highest to lowest)
          return Math.abs(b.arrChange) - Math.abs(a.arrChange);
        });
    },

    closeArrCustomerList() {
      this.selectedArrCategory = null;
      this.selectedArrDataSource = null;
      this.arrCustomerListData.customers = [];
      this.arrCustomerListData.sortColumn = null;
      this.arrCustomerListData.sortDirection = 'desc';
    },

    openArrDetailsModal() {
      this.showArrDetailsModal = true;
      this.$nextTick(() => {
        this.renderArrChartModal();
      });
    },

    closeArrDetailsModal() {
      this.showArrDetailsModal = false;
    },

    async openScheduleChangesModal() {
      this.scheduleChangesModal.show = true;
      this.scheduleChangesModal.changes = [];
      this.scheduleChangesModal.offset = 0;
      this.scheduleChangesModal.hasMore = true;
      await this.fetchScheduleChanges();
    },

    closeScheduleChangesModal() {
      this.scheduleChangesModal.show = false;
      this.scheduleChangesModal.changes = [];
      this.scheduleChangesModal.offset = 0;
      this.scheduleChangesModal.hasMore = true;
    },

    async fetchScheduleChanges(append = false) {
      if (append) {
        this.scheduleChangesModal.loadingMore = true;
      } else {
        this.scheduleChangesModal.loading = true;
      }

      try {
        const data = {
          query: `
            query($year: Int!, $brand: String, $limit: Int, $offset: Int) {
              invoiceScheduleChanges(year: $year, brand: $brand, limit: $limit, offset: $offset)
            }
          `,
          variables: {
            year: this.primaryYear,
            brand: this.selectedBrand || "all",
            limit: this.scheduleChangesModal.limit,
            offset: this.scheduleChangesModal.offset
          }
        };

        const response = await this.customFetch(data);
        if (response && response.invoiceScheduleChanges) {
          const parsedData = JSON.parse(response.invoiceScheduleChanges);

          if (append) {
            this.scheduleChangesModal.changes.push(...parsedData);
          } else {
            this.scheduleChangesModal.changes = parsedData;
          }

          // Check if there are more results
          if (parsedData.length < this.scheduleChangesModal.limit) {
            this.scheduleChangesModal.hasMore = false;
          }

          this.scheduleChangesModal.offset += parsedData.length;
        }
      } catch (error) {
        console.error("Error fetching schedule changes:", error);
      } finally {
        this.scheduleChangesModal.loading = false;
        this.scheduleChangesModal.loadingMore = false;
      }
    },

    handleScheduleChangesScroll(event) {
      const element = event.target;
      const scrollPosition = element.scrollTop + element.clientHeight;
      const scrollHeight = element.scrollHeight;

      // Load more when user is 100px from bottom
      if (scrollHeight - scrollPosition < 100 && this.scheduleChangesModal.hasMore && !this.scheduleChangesModal.loadingMore) {
        this.fetchScheduleChanges(true);
      }
    },

    formatChangeType(type) {
      const typeMap = {
        created: 'Created',
        price_change: 'Price Change',
        status_change: 'Status Change',
        deleted: 'Deleted'
      };
      return typeMap[type] || type;
    },

    sortArrCustomers(column) {
      if (this.arrCustomerListData.sortColumn === column) {
        // Toggle direction if same column
        this.arrCustomerListData.sortDirection =
          this.arrCustomerListData.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        // New column, default to desc
        this.arrCustomerListData.sortColumn = column;
        this.arrCustomerListData.sortDirection = 'desc';
      }
    },

    exportArrCustomersToExcel() {
      if (!this.arrCustomerListData.customers || this.arrCustomerListData.customers.length === 0) {
        alert('No data to export');
        return;
      }

      // Determine filename based on category
      let filename = 'arr_customers.csv';
      if (this.selectedArrCategory === 'new') {
        filename = 'arr_new_customers.csv';
      } else if (this.selectedArrCategory === 'existing') {
        filename = 'arr_existing_customers_growth.csv';
      } else if (this.selectedArrCategory === 'removed') {
        filename = 'arr_removed_customers.csv';
      } else if (this.selectedArrCategory === 'all-existing') {
        filename = 'arr_all_existing_customers.csv';
      }

      // Create CSV content
      let csvContent = '';

      // Add headers based on category
      if (this.selectedArrCategory === 'new') {
        csvContent += 'Customer Name,' + this.primaryYear + ' ARR,Scheduled,Total\n';
      } else {
        csvContent += 'Customer Name,' + this.primaryYear + ' ARR,Scheduled,' + this.comparisonYear + ' ARR,Change\n';
      }

      // Add rows
      this.arrCustomerListData.customers.forEach(customer => {
        const name = '"' + (customer.name || '').replace(/"/g, '""') + '"';

        if (this.selectedArrCategory === 'new') {
          csvContent += `${name},${customer.currentArrCents},${customer.currentScheduledCents},${customer.totalCents}\n`;
        } else {
          const changeCents = customer.arrChangeCents >= 0 ? '+' + customer.arrChangeCents : customer.arrChangeCents;
          csvContent += `${name},${customer.currentArrCents},${customer.currentScheduledCents},${customer.previousArrCents},${changeCents}\n`;
        }
      });

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },

    renderArrChart() {
      if (!this.$refs.arrChart) return;

      // Destroy existing chart if it exists
      if (this.arrChartInstance) {
        this.arrChartInstance.destroy();
      }

      const ctx = this.$refs.arrChart.getContext('2d');

      this.arrChartInstance = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: [this.primaryYear.toString(), this.comparisonYear.toString()],
          datasets: [
            {
              label: 'ARR Start',
              data: [
                this.arrStartData.currentYear / 1000000,
                this.arrStartData.previousYear / 1000000
              ],
              backgroundColor: '#FC8181',
              stack: 'Stack 0'
            },
            {
              label: 'Invoiced',
              data: [
                (this.primaryYearRevenueData.invoiced - this.arrStartData.currentYear) / 1000000,
                (this.comparisonYearRevenueData.invoiced - this.arrStartData.previousYear) / 1000000
              ],
              backgroundColor: '#2D3748',
              stack: 'Stack 0'
            },
            {
              label: 'Scheduled',
              data: [
                this.primaryYearRevenueData.scheduled / 1000000,
                this.comparisonYearRevenueData.scheduled / 1000000
              ],
              backgroundColor: '#48BB78',
              stack: 'Stack 0'
            },
            {
              label: 'ARR End',
              type: 'line',
              data: [
                this.primaryYearRevenueData.total / 1000000,
                this.comparisonYearRevenueData.total / 1000000
              ],
              borderColor: '#38B2AC',
              backgroundColor: 'transparent',
              borderWidth: 3,
              pointRadius: 6,
              pointBackgroundColor: '#38B2AC',
              pointBorderColor: '#fff',
              pointBorderWidth: 2,
              fill: false
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            xAxes: [{
              stacked: true,
              gridLines: {
                display: false
              }
            }],
            yAxes: [{
              stacked: true,
              ticks: {
                beginAtZero: true,
                callback: (value) => '€' + value.toFixed(0) + 'M'
              },
              gridLines: {
                color: '#E2E8F0'
              }
            }]
          },
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              boxWidth: 12,
              padding: 15
            }
          },
          tooltips: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: (tooltipItem, data) => {
                const label = data.datasets[tooltipItem.datasetIndex].label || '';
                const value = tooltipItem.yLabel;
                return label + ': €' + value.toFixed(2) + 'M';
              }
            }
          }
        }
      });
    },

    renderArrChartModal() {
      if (!this.$refs.arrChartModal) return;

      // Destroy existing chart if it exists
      if (this.arrChartModalInstance) {
        this.arrChartModalInstance.destroy();
      }

      const ctx = this.$refs.arrChartModal.getContext('2d');

      this.arrChartModalInstance = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: [this.primaryYear.toString(), this.comparisonYear.toString()],
          datasets: [
            {
              label: 'ARR Start',
              data: [
                this.arrStartData.currentYear / 1000000,
                this.arrStartData.previousYear / 1000000
              ],
              backgroundColor: '#FC8181',
              stack: 'Stack 0'
            },
            {
              label: 'Invoiced',
              data: [
                (this.primaryYearRevenueData.invoiced - this.arrStartData.currentYear) / 1000000,
                (this.comparisonYearRevenueData.invoiced - this.arrStartData.previousYear) / 1000000
              ],
              backgroundColor: '#2D3748',
              stack: 'Stack 0'
            },
            {
              label: 'Scheduled',
              data: [
                this.primaryYearRevenueData.scheduled / 1000000,
                this.comparisonYearRevenueData.scheduled / 1000000
              ],
              backgroundColor: '#48BB78',
              stack: 'Stack 0'
            },
            {
              label: 'ARR End',
              type: 'line',
              data: [
                this.primaryYearRevenueData.total / 1000000,
                this.comparisonYearRevenueData.total / 1000000
              ],
              borderColor: '#38B2AC',
              backgroundColor: 'transparent',
              borderWidth: 3,
              pointRadius: 6,
              pointBackgroundColor: '#38B2AC',
              pointBorderColor: '#fff',
              pointBorderWidth: 2,
              fill: false
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            xAxes: [{
              stacked: true,
              gridLines: {
                display: false
              }
            }],
            yAxes: [{
              stacked: true,
              ticks: {
                beginAtZero: true,
                callback: (value) => '€' + value.toFixed(0) + 'M'
              },
              gridLines: {
                color: '#E2E8F0'
              }
            }]
          },
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              boxWidth: 12,
              padding: 15
            }
          },
          tooltips: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: (tooltipItem, data) => {
                const label = data.datasets[tooltipItem.datasetIndex].label || '';
                const value = tooltipItem.yLabel;
                return label + ': €' + value.toFixed(2) + 'M';
              }
            }
          }
        }
      });
    }
  },

  async created() {
    // Initialize selectedBrand based on current user's brand
    const currentBrand = this.getUserSystem.toLowerCase();
    if (currentBrand !== 'ulubv' && currentBrand !== 'all') {
      this.selectedBrand = currentBrand;
    }

    await Promise.all([
      this.fetchRevenueToDate(),
      this.fetchRecentRevenueData(),
      this.fetchCreditsData(),
      this.fetchArrStartData(),
      this.fetchArrStartYoyData(),
      this.fetchArrStartWithChurnData(),
      this.fetchActiveCustomersData(),
      this.fetchPrimaryYearRevenueData(),
      this.fetchComparisonYearRevenueData(),
      this.fetchInvoiceItemsByType(),
      this.fetchArrChangesFromInvoices()
    ]);
  },

  mounted() {
    this.$nextTick(() => {
      this.renderArrChart();
    });
  },

  watch: {
    primaryYearRevenueData: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.renderArrChart();
        });
      }
    },
    comparisonYearRevenueData: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.renderArrChart();
        });
      }
    },
    arrStartData: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.renderArrChart();
        });
      }
    }
  },

  beforeDestroy() {
    if (this.arrChartInstance) {
      this.arrChartInstance.destroy();
    }
    if (this.arrChartModalInstance) {
      this.arrChartModalInstance.destroy();
    }
  }
};
</script>

<style scoped>
.overview-dashboard {
  padding: 24px;
  background-color: #f7f7f7;
  min-height: calc(100vh - 100px);
}

/* Header Controls */
.dashboard-header {
  margin-bottom: 24px;
}

.header-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.brand-filter {
  display: flex;
  align-items: center;
  gap: 12px;
}

.brand-filter label {
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
}

.brand-selector {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  color: #2d3748;
  background: white;
  cursor: pointer;
  outline: none;
}

.brand-selector:focus {
  border-color: #48bb78;
}

.year-selectors-container {
  display: flex;
  align-items: center;
  gap: 16px;
}

.year-selector-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.year-selector-group label {
  font-size: 14px;
  font-weight: 500;
  color: #4a5568;
}

.year-selector {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  color: #2d3748;
  background: white;
  cursor: pointer;
  outline: none;
  min-width: 80px;
}

.year-selector:focus {
  border-color: #48bb78;
}

.vs-separator {
  font-size: 14px;
  font-weight: 600;
  color: #a0aec0;
  padding: 0 8px;
}

/* Active Customers Card */
.active-customers-card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.active-customers-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.active-customers-title {
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.active-customers-icon {
  font-size: 24px;
  color: #48bb78;
}

.active-customers-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.active-customers-value {
  font-size: 36px;
  font-weight: 700;
  color: #2d3748;
  line-height: 1;
}

.active-customers-label {
  font-size: 14px;
  color: #718096;
  margin-bottom: 8px;
}

.active-customers-comparison {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
}

.comparison-year-label {
  color: #718096;
  font-weight: 500;
}

.comparison-value {
  color: #4a5568;
  font-weight: 600;
}

.comparison-change {
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.comparison-change.positive {
  color: #22543d;
  background-color: #c6f6d5;
}

.comparison-change.negative {
  color: #742a2a;
  background-color: #fed7d7;
}

/* Top Grid - 3 columns */
.top-grid {
  display: grid;
  grid-template-columns: 1fr 1.5fr 1.5fr;
  gap: 20px;
  margin-bottom: 20px;
}

/* Card Base Styles */
.card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 16px 0;
}

.card-title-with-button {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.card-title-with-button .card-title {
  margin: 0;
}

.card-title-with-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.card-title-with-actions .card-title {
  margin: 0;
}

.refresh-button {
  background: transparent;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
  transition: all 0.2s;
  color: #4a5568;
  font-size: 14px;
}

.refresh-button:hover:not(:disabled) {
  background: #f7fafc;
  border-color: #a0aec0;
  color: #2d3748;
}

.refresh-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.refresh-button .fa-icon {
  transition: transform 0.3s ease;
}

.refresh-button .fa-icon.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Revenue Overview Container (Top Row) */
.revenue-overview-container {
  margin-bottom: 20px;
}

.revenue-overview-merged {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 30px;
}

.revenue-comparison-section {
  border-right: 1px solid #e2e8f0;
  padding-right: 30px;
}

.revenue-composition-section-merged {
  display: flex;
  flex-direction: column;
  padding-left: 30px;
}

.revenue-composition-container-merged {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 30px;
  align-items: center;
  flex: 1;
}

/* Recent Revenue Card */
.recent-revenue-card {
  margin-bottom: 20px;
}

.recent-revenue-current-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 12px;
}

.recent-revenue-stat {
  padding: 16px;
  background: #f7fafc;
  border-radius: 8px;
  border-left: 3px solid #3182CE;
}

.recent-revenue-label {
  font-size: 12px;
  font-weight: 600;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
}

.recent-revenue-value {
  font-size: 26px;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 2px;
}

.recent-revenue-sublabel {
  font-size: 11px;
  color: #a0aec0;
}

.recent-revenue-scheduled {
  margin-top: 8px;
  font-size: 12px;
  color: #805AD5;
  border-top: 1px solid #e2e8f0;
  padding-top: 6px;
}

.recent-revenue-total {
  display: block;
  font-weight: 700;
  color: #2d3748;
  margin-top: 2px;
}

.recent-revenue-total-change {
  font-size: 12px;
  font-weight: 600;
  margin-left: 4px;
}

.recent-revenue-total-change.positive {
  color: #38a169;
}

.recent-revenue-total-change.negative {
  color: #e53e3e;
}

.recent-revenue-comparison {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.recent-revenue-ly {
  font-size: 13px;
  color: #4a5568;
  font-weight: 500;
}

.recent-revenue-change {
  font-size: 13px;
  font-weight: 700;
}

.recent-revenue-change.positive {
  color: #38a169;
}

.recent-revenue-change.negative {
  color: #e53e3e;
}

.recent-revenue-next-year {
  margin-top: 6px;
  font-size: 12px;
  color: #805AD5;
  font-weight: 600;
}

.recent-revenue-upcoming-section {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
}

.recent-revenue-upcoming-stat {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 8px 12px;
  background: #f7fafc;
  border-radius: 6px;
  border-left: 3px solid #805AD5;
}

.recent-revenue-upcoming-label {
  font-size: 12px;
  font-weight: 600;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

.recent-revenue-upcoming-value {
  font-size: 16px;
  font-weight: 700;
  color: #2d3748;
}

.recent-revenue-upcoming-sublabel {
  font-size: 11px;
  color: #a0aec0;
}

/* Second Row Grid (Credits + Invoice Items) */
.second-row-grid {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 20px;
  margin-bottom: 20px;
}

/* Revenue Card */
.revenue-card {
  display: flex;
  flex-direction: column;
}

.comparison-year-reference {
  font-size: 13px;
  color: #718096;
  font-weight: 500;
  margin-top: 4px;
}

/* Credits Card */
.credits-card {
  display: flex;
  flex-direction: column;
}

.credits-header {
  margin-bottom: 16px;
}

.credits-summary {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.credits-value {
  font-size: 24px;
  font-weight: 700;
  color: #2d3748;
}

.credits-change {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.credits-change.positive {
  background-color: #c6f6d5;
  color: #22543d;
}

.credits-change.negative {
  background-color: #fed7d7;
  color: #742a2a;
}

.credits-content {
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 24px;
  align-items: center;
}

.credits-chart {
  width: 180px;
  height: 180px;
  cursor: pointer;
}

.credits-chart canvas {
  max-width: 100%;
  max-height: 100%;
  cursor: pointer;
}

.credits-data {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 250px;
  overflow-y: auto;
  padding-right: 20px;
}

.credits-header-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 2px solid #e2e8f0;
  font-size: 10px;
  font-weight: 700;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.credits-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 12px;
}

.month-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #4a5568;
  font-weight: 500;
}

.credits-data .color-indicator {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex-shrink: 0;
}

.amount-value {
  color: #2d3748;
  font-weight: 600;
}

/* Invoice Items Card */
.invoice-items-card {
  display: flex;
  flex-direction: column;
}

.invoice-items-header {
  margin-bottom: 16px;
}

.invoice-items-summary {
  margin-top: 8px;
}

.invoice-items-value {
  font-size: 24px;
  font-weight: 700;
  color: #2d3748;
}

.invoice-items-content {
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 24px;
  align-items: center;
}

.invoice-items-chart {
  width: 180px;
  height: 180px;
}

.invoice-items-chart canvas {
  max-width: 100%;
  max-height: 100%;
}

.invoice-items-data {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 250px;
  overflow-y: auto;
  padding-right: 20px;
}

.invoice-items-header-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 2px solid #e2e8f0;
  font-size: 10px;
  font-weight: 700;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.invoice-items-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 12px;
}

.type-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #4a5568;
  font-weight: 500;
}

.invoice-items-data .color-indicator {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex-shrink: 0;
}

/* Invoice Items Comparison Styles */
.invoice-items-header-row-comparison {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 2px solid #e2e8f0;
  font-size: 10px;
  font-weight: 600;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.invoice-items-row-comparison {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 12px;
  padding: 6px 0;
  font-size: 12px;
  align-items: center;
}

.invoice-items-header-row-comparison .year-column,
.invoice-items-header-row-comparison .change-column {
  text-align: right;
}

.invoice-items-row-comparison .amount-value {
  text-align: right;
  color: #2d3748;
  font-weight: 500;
}

.invoice-items-row-comparison .change-value {
  text-align: right;
  font-weight: 600;
  font-size: 12px;
}

.invoice-items-row-comparison .change-value.positive {
  color: #48bb78;
}

.invoice-items-row-comparison .change-value.negative {
  color: #e53e3e;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 900px;
  width: 100%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.modal-content.arr-details-modal {
  max-width: 1200px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #2d3748;
}

.close-button {
  background: none;
  border: none;
  font-size: 20px;
  color: #718096;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: background-color 0.2s;
}

.close-button:hover {
  background-color: #f7fafc;
  color: #2d3748;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.modal-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background-color: #f7fafc;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #e2e8f0;
}

.modal-summary-label {
  font-size: 16px;
  font-weight: 600;
  color: #4a5568;
}

.modal-summary-value {
  font-size: 20px;
  font-weight: 700;
  color: #2d3748;
}

.loading-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #718096;
}

.no-data {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #a0aec0;
  font-style: italic;
}

.breakdown-table-container {
  overflow-x: auto;
}

.breakdown-table {
  width: 100%;
  border-collapse: collapse;
}

.breakdown-table thead th {
  background: #f7fafc;
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e2e8f0;
}

.breakdown-table thead th.sortable {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s, color 0.2s;
  position: relative;
}

.breakdown-table thead th.sortable:hover {
  background-color: #edf2f7;
  color: #2d3748;
}

.breakdown-table thead th.sortable i {
  margin-left: 6px;
  opacity: 0.5;
  transition: opacity 0.2s;
  font-size: 11px;
}

.breakdown-table thead th.sortable:hover i {
  opacity: 1;
}

.breakdown-table thead th.sortable i.fa-sort-up,
.breakdown-table thead th.sortable i.fa-sort-down {
  opacity: 1;
  color: #48bb78;
}

.breakdown-table tbody tr {
  border-bottom: 1px solid #e2e8f0;
  transition: background-color 0.2s;
}

.breakdown-table tbody tr:hover {
  background-color: #f7fafc;
}

.breakdown-table tbody td {
  padding: 12px 16px;
  font-size: 14px;
  color: #2d3748;
}

.breakdown-table .price-cell {
  font-weight: 600;
  color: #e53e3e;
  text-align: right;
}

/* Hierarchical table styles */
.breakdown-table.hierarchical .type-header-row {
  background-color: #f7fafc;
  border-top: 2px solid #cbd5e0;
}

.breakdown-table.hierarchical .type-header-row:first-child {
  border-top: none;
}

.breakdown-table.hierarchical .type-header-row.clickable {
  cursor: pointer;
  user-select: none;
}

.breakdown-table.hierarchical .type-header-row.clickable:hover {
  background-color: #edf2f7;
}

.breakdown-table.hierarchical .type-header {
  padding: 14px 16px;
  font-size: 15px;
  color: #2d3748;
}

.breakdown-table.hierarchical .toggle-icon {
  margin-right: 12px;
  font-size: 16px;
  color: #2d3748;
  transition: all 0.2s ease;
  display: inline-block;
  width: 16px;
  font-weight: bold;
  vertical-align: middle;
}

.breakdown-table.hierarchical .type-total {
  font-size: 15px;
  color: #2d3748;
}

.breakdown-table.hierarchical .name-row {
  background-color: #ffffff;
}

.breakdown-table.hierarchical .name-row:hover {
  background-color: #f7fafc;
}

.breakdown-table.hierarchical .name-cell {
  padding-left: 48px;
  color: #4a5568;
  font-size: 13px;
}

/* Responsive Design */
@media (max-width: 1400px) {
  .top-grid {
    grid-template-columns: 1fr;
  }

  .arr-grid {
    grid-template-columns: 1fr;
  }

  .breakdown-table-header,
  .breakdown-table-row {
    grid-template-columns: 1fr 1fr 1fr 1.2fr;
    gap: 8px;
    padding: 12px 12px;
  }

  .breakdown-table-header > div,
  .row-label,
  .row-value,
  .row-change {
    font-size: 12px;
  }
}

@media (max-width: 768px) {
  .top-grid {
    grid-template-columns: 1fr;
  }

  .arr-grid {
    grid-template-columns: 1fr;
  }

  .credits-content {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .credits-chart {
    height: auto;
    max-width: 300px;
    margin: 0 auto;
  }

  .invoice-items-content {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .invoice-items-chart {
    height: auto;
    max-width: 300px;
    margin: 0 auto;
  }
}

/* ARR Section */
.arr-section {
  margin-top: 20px;
}

/* ARR Merged Widget */
.arr-merged-card {
  width: 100%;
}

.arr-merged-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  align-items: stretch;
}

.arr-merged-table {
  display: flex;
  flex-direction: column;
}

.arr-merged-chart {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 20px;
  background-color: #f7fafc;
  border-radius: 8px;
}

.arr-merged-header {
  display: grid;
  grid-template-columns: 2fr 1.5fr 1.5fr 2fr;
  gap: 16px;
  padding: 14px 20px;
  background-color: #f7fafc;
  border-radius: 8px 8px 0 0;
}

.arr-merged-header > div {
  font-size: 13px;
  font-weight: 700;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.header-metric {
  text-align: left;
}

.header-year,
.header-change {
  text-align: right;
}

.arr-merged-row {
  display: grid;
  grid-template-columns: 2fr 1.5fr 1.5fr 2fr;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  align-items: center;
}

.arr-merged-row:hover {
  background-color: #f7fafc;
}

.arr-merged-row.start-row {
  background-color: #EBF8FF;
  font-weight: 600;
}

.arr-merged-row.end-row {
  background-color: #E6FFFA;
  font-weight: 600;
}

.arr-merged-row.growth-row {
  background-color: #F0FFF4;
  border-bottom: none;
  border-top: 3px solid #48bb78;
  font-weight: 700;
  font-size: 15px;
}

.arr-merged-row.churn-row {
  background-color: #FFF5F5;
  border-top: 3px solid #e53e3e;
  font-weight: 700;
  font-size: 15px;
}

.churn-value {
  color: #e53e3e;
}

.row-metric {
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  text-align: left;
}

.row-metric.indent {
  padding-left: 24px;
  font-size: 13px;
  font-weight: 500;
  color: #4a5568;
}

.row-metric.credit-label {
  color: #e53e3e;
}

.row-year-value {
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  text-align: right;
}

.row-year-value.credit-value {
  color: #e53e3e;
}

.row-change-value.credit-value {
  color: #e53e3e;
}

.row-year-value.growth-value {
  font-size: 16px;
  font-weight: 700;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.row-year-value.growth-value.positive {
  color: #48bb78;
}

.row-year-value.growth-value.negative {
  color: #e53e3e;
}

.growth-percent {
  font-size: 12px;
  font-weight: 600;
  opacity: 0.85;
}

.row-change-value {
  font-size: 13px;
  font-weight: 600;
  text-align: right;
}

.row-change-value.positive {
  color: #48bb78;
}

.row-change-value.negative {
  color: #e53e3e;
}

.section-title {
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.section-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.section-title h3 {
  font-size: 18px;
  font-weight: 700;
  color: #2d3748;
  margin: 0;
}

.details-button {
  padding: 8px 16px;
  background-color: #48bb78;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background-color 0.2s;
}

.details-button:hover {
  background-color: #38a169;
}

.details-button i {
  font-size: 14px;
}

.arr-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.arr-card-title {
  font-size: 14px;
  font-weight: 600;
  color: #4a5568;
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* ARR Start Card */
.arr-comparison-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.arr-year-block {
  flex: 1;
  text-align: center;
  padding: 16px;
  background: #f7fafc;
  border-radius: 8px;
}

.arr-year-label {
  font-size: 12px;
  color: #718096;
  font-weight: 600;
  margin-bottom: 8px;
}

.arr-value {
  font-size: 22px;
  font-weight: 700;
  color: #2d3748;
}

.arr-change-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.arr-change-indicator i {
  font-size: 24px;
}

.arr-change-indicator span {
  font-size: 14px;
  font-weight: 700;
}

.arr-change-indicator span.positive {
  color: #48bb78;
}

.arr-change-indicator span.negative {
  color: #e53e3e;
}

/* ARR Growth Card */
.arr-growth-container {
  display: flex;
  gap: 16px;
}

.growth-year-block {
  flex: 1;
  padding: 16px;
  background: #f7fafc;
  border-radius: 8px;
}

.growth-year {
  font-size: 14px;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 12px;
  text-align: center;
}

.growth-calculation {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  color: #4a5568;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.growth-calculation .operator {
  font-weight: 700;
  color: #718096;
}

.growth-result {
  text-align: center;
  font-size: 18px;
  font-weight: 700;
}

.growth-result.positive {
  color: #48bb78;
}

.growth-result.negative {
  color: #e53e3e;
}

.growth-result .percentage {
  display: block;
  font-size: 12px;
  margin-top: 4px;
}

.growth-divider {
  width: 1px;
  background: #e2e8f0;
}

/* ARR Breakdown Card - Table Layout */
.arr-breakdown-table {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.breakdown-table-header {
  display: grid;
  grid-template-columns: 1fr 1.2fr 1.2fr 1.5fr;
  gap: 16px;
  padding: 12px 16px;
  background-color: #f7fafc;
  border-radius: 8px 8px 0 0;
}

.breakdown-table-header > div {
  font-size: 13px;
  font-weight: 700;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.header-label {
  text-align: left;
}

.header-year,
.header-change {
  text-align: right;
}

.breakdown-table-row {
  display: grid;
  grid-template-columns: 1fr 1.2fr 1.2fr 1.5fr;
  gap: 16px;
  padding: 14px 16px;
  border-bottom: 1px solid #e2e8f0;
  align-items: center;
}

.breakdown-table-row:hover {
  background-color: #f7fafc;
}

.breakdown-table-row.total-row {
  border-bottom: none;
  border-top: 2px solid #e2e8f0;
  margin-top: 4px;
  font-weight: 700;
}

.row-label {
  font-size: 14px;
  font-weight: 600;
  color: #4a5568;
  text-align: left;
}

.row-value {
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  text-align: right;
}

.total-row .row-label,
.total-row .row-value {
  font-size: 15px;
  font-weight: 700;
}

.row-change {
  font-size: 14px;
  font-weight: 600;
  text-align: right;
}

.row-change.positive {
  color: #48bb78;
}

.row-change.negative {
  color: #e53e3e;
}

.total-row .row-change {
  font-size: 15px;
  font-weight: 700;
}

/* Revenue Card Comparison Styles */
.revenue-comparison-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.revenue-comparison-header {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e2e8f0;
}

.year-label {
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.year-label.primary {
  font-size: 16px;
  font-weight: 700;
}

.year-label.comparison {
  font-size: 12px;
  font-weight: 600;
}

.revenue-metric-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.metric-label {
  font-size: 11px;
  font-weight: 500;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metric-values {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 16px;
  align-items: center;
}

.value-item {
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
}

.value-item.primary-total {
  font-size: 20px;
  font-weight: 700;
}

.value-item.change {
  text-align: right;
}

.value-item.positive {
  color: #48bb78;
}

.value-item.negative {
  color: #e53e3e;
}

/* Responsive Design */
@media (max-width: 768px) {
  .metric-values {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .value-item {
    font-size: 12px;
  }

  .value-item.change {
    text-align: left;
  }
}

/* Revenue Composition Section */
.revenue-composition-card {
  padding: 24px;
}

.revenue-composition-container {
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 30px;
  align-items: center;
}

.revenue-composition-chart {
  width: 180px;
  height: 180px;
  margin: 0 auto;
}

.revenue-composition-container-merged .revenue-composition-chart {
  width: 200px;
  height: 200px;
}

.revenue-composition-container-merged .revenue-composition-data {
  gap: 14px;
}

.revenue-composition-container-merged .revenue-composition-item {
  padding: 10px 0;
  grid-template-columns: 2fr 1fr 80px;
}

.revenue-composition-container-merged .revenue-composition-value {
  font-size: 15px;
}

.revenue-composition-container-merged .revenue-composition-percent {
  font-size: 14px;
}

.revenue-composition-container-merged .revenue-composition-text {
  font-size: 13px;
}

.revenue-composition-container-merged .revenue-composition-text strong {
  font-size: 15px;
  font-weight: 700;
}

.revenue-composition-container-merged .revenue-composition-text.indent {
  font-size: 12px;
}

.revenue-composition-container-merged .revenue-composition-item.sub-item .revenue-composition-value,
.revenue-composition-container-merged .revenue-composition-item.sub-item .revenue-composition-percent {
  font-size: 13px;
}

.revenue-composition-container-merged .revenue-composition-color {
  width: 14px;
  height: 14px;
}

.revenue-composition-data {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.revenue-composition-item {
  display: grid;
  grid-template-columns: 2fr 1fr 80px;
  gap: 16px;
  align-items: center;
  padding: 12px 0;
}

.revenue-composition-item.sub-item {
  padding: 8px 12px;
  background-color: #f7fafc;
  margin: 4px 0;
  border-radius: 6px;
}

.revenue-composition-item.sub-item .revenue-composition-value,
.revenue-composition-item.sub-item .revenue-composition-percent {
  font-size: 14px;
}

.revenue-composition-item.total {
  border-top: 2px solid #2d3748;
  font-weight: 700;
  padding-top: 16px;
  margin-top: 4px;
}

.revenue-composition-label {
  display: flex;
  align-items: center;
  gap: 10px;
}

.revenue-composition-color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  flex-shrink: 0;
}

.revenue-composition-text {
  font-size: 14px;
  color: #2d3748;
  font-weight: 500;
}

.revenue-composition-text.indent {
  font-size: 13px;
  color: #4a5568;
}

.revenue-composition-value {
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  text-align: right;
}

.revenue-composition-percent {
  font-size: 14px;
  font-weight: 600;
  color: #718096;
  text-align: right;
}

.revenue-composition-item.total .revenue-composition-value {
  font-size: 18px;
  color: #2d3748;
}

.revenue-composition-item.total .revenue-composition-percent {
  font-size: 16px;
  color: #2d3748;
}

.revenue-composition-divider {
  height: 1px;
  background-color: #e2e8f0;
  margin: 8px 0;
}

/* ARR Analysis Section */
.arr-analysis-section {
  margin-top: 20px;
}

.arr-analysis-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.arr-analysis-card {
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative;
}

.arr-analysis-card:not(.merged-card) {
  cursor: pointer;
}

.arr-analysis-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.arr-analysis-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.arr-analysis-title {
  font-size: 13px;
  font-weight: 600;
  color: #4a5568;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex: 1;
}

.arr-analysis-icon {
  font-size: 24px;
  color: #a0aec0;
  opacity: 0.5;
}

.arr-analysis-value {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 8px;
}

.arr-analysis-value.positive {
  color: #48bb78;
}

.arr-analysis-value.negative {
  color: #e53e3e;
}

.arr-analysis-value.neutral {
  color: #3182CE;
}

.arr-analysis-count {
  font-size: 13px;
  color: #718096;
  font-weight: 500;
}

.arr-analysis-scheduled {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  margin-bottom: 8px;
}

.scheduled-label {
  font-size: 11px;
  color: #718096;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.scheduled-amount {
  font-size: 14px;
  color: #38B2AC;
  font-weight: 600;
}

.scheduled-amount.scheduled-lost {
  color: #E53E3E;
}

.delta-label {
  font-size: 11px;
  color: #718096;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-right: 6px;
}

.delta-positive {
  font-size: 16px;
  color: #48BB78;
  font-weight: 700;
}

.delta-negative {
  font-size: 16px;
  color: #E53E3E;
  font-weight: 700;
}

.removed-amount {
  font-size: 18px;
  font-weight: 700;
  color: #E53E3E;
}

.clickable-amount {
  cursor: pointer;
  transition: opacity 0.2s;
}

.clickable-amount:hover {
  opacity: 0.8;
}

.growth-info-inline {
  font-size: 12px;
  color: #718096;
  font-weight: 400;
  margin-left: 8px;
}

/* Merged Card Styles */
.arr-analysis-subvalue {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  margin-bottom: 8px;
}

.arr-analysis-subvalue .added-amount {
  font-size: 16px;
  font-weight: 600;
}

.arr-analysis-subvalue .added-amount.positive {
  color: #48bb78;
}

.arr-analysis-subvalue .added-percentage {
  font-size: 13px;
  font-weight: 600;
  color: #48bb78;
  background-color: #C6F6D5;
  padding: 2px 8px;
  border-radius: 12px;
}

.growth-info {
  font-size: 11px;
  color: #a0aec0;
  font-weight: 400;
  margin-left: 4px;
}

/* ARR Customer List Modal Styles */
.modal-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.export-button {
  padding: 8px 16px;
  background-color: #48bb78;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background-color 0.2s;
}

.export-button:hover {
  background-color: #38a169;
}

.export-button i {
  font-size: 16px;
}

.arr-change-positive {
  color: #48bb78;
  font-weight: 600;
}

.arr-change-negative {
  color: #e53e3e;
  font-weight: 600;
}

@media (max-width: 1200px) {
  .arr-analysis-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .arr-analysis-grid {
    grid-template-columns: 1fr;
  }

  .arr-analysis-subvalue {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
}

/* Button Group */
.button-group {
  display: flex;
  gap: 8px;
  align-items: center;
}

.changes-button {
  background: #4299e1;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  transition: all 0.2s;
  color: white;
  font-size: 13px;
  font-weight: 500;
}

.changes-button:hover {
  background: #3182ce;
}

/* Schedule Changes Modal */
.schedule-changes-container {
  max-height: 600px;
  overflow: hidden;
}

.schedule-changes-list {
  max-height: 600px;
  overflow-y: auto;
  padding-right: 10px;
}

.schedule-change-item {
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 8px;
  transition: box-shadow 0.2s;
}

.schedule-change-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.change-line-1 {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.change-customer {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.change-customer strong {
  font-size: 14px;
  color: #2d3748;
  font-weight: 600;
}

.order-number {
  font-size: 12px;
  color: #718096;
}

.change-revenue-impact {
  font-size: 16px;
  font-weight: 700;
}

.change-revenue-impact.positive {
  color: #48bb78;
}

.change-revenue-impact.negative {
  color: #e53e3e;
}

.change-line-2 {
  display: flex;
  align-items: center;
}

.change-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #718096;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.meta-label {
  color: #a0aec0;
}

.meta-value {
  color: #4a5568;
  font-weight: 500;
}

.meta-separator {
  color: #cbd5e0;
}

.change-types {
  display: inline;
}

.load-more-trigger {
  height: 1px;
}

.loading-more {
  text-align: center;
  padding: 16px;
  color: #718096;
  font-size: 14px;
}

.large-modal .modal-content {
  max-width: 800px;
  width: 90%;
}
</style>
